import styled, { keyframes, css } from 'styled-components';
import PropTypes from '@userzoomlibraries/toolkit-js-utils/prop-types';
import React, { useRef, useState, useMemo, useCallback, useEffect, useImperativeHandle, Fragment } from 'react';
import isDefinedAndNotNull from '@userzoomlibraries/toolkit-js-utils/is-defined-and-not-null';
import noop from '@userzoomlibraries/toolkit-js-utils/noop';
import isFunction from '@userzoomlibraries/toolkit-js-utils/is-function';
import isString from '@userzoomlibraries/toolkit-js-utils/is-string';
import getId from '@userzoomlibraries/toolkit-js-utils/get-id';
import isNull from '@userzoomlibraries/toolkit-js-utils/is-null';
import getSafeHtmlElement from '@userzoomlibraries/toolkit-js-utils/get-safe-html-element';
import getScrollableContainersByElement from '@userzoomlibraries/toolkit-js-utils/get-scrollable-containers-by-element';
import usePrevious from '@userzoomlibraries/toolkit-react-utils/use-previous';
import useEscape from '@userzoomlibraries/toolkit-react-utils/use-escape';
import useOnClickOutside from '@userzoomlibraries/toolkit-react-utils/use-on-click-outside';
import useMutationObserver from '@userzoomlibraries/toolkit-react-utils/use-mutation-observer';
import useDataAttributes from '@userzoomlibraries/toolkit-react-utils/use-data-attributes';
import useDisableBackgroundScroll from '@userzoomlibraries/toolkit-react-utils/use-disable-background-scroll';
import { F as FloatingElement } from './index.styled.js';
import isNumber from '@userzoomlibraries/toolkit-js-utils/is-number';
import floatRound from '@userzoomlibraries/toolkit-js-utils/float-round';
import isHTMLElement from '@userzoomlibraries/toolkit-js-utils/is-html-element';
import getVisibilityPercentageOfElement from '@userzoomlibraries/toolkit-js-utils/get-visibility-percentage-of-element';
import getRelativePositionToParent from '@userzoomlibraries/toolkit-js-utils/get-relative-position-to-parent';
import { jsxs, jsx } from 'react/jsx-runtime';

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var Wrapper = styled.span.attrs({
  tabIndex: 0
}).withConfig({
  componentId: "gz-floating-element-1679042011924__sc-18dkrnx-0"
})([""]);
Wrapper.displayName = 'Gearz.LinkedFloatingElement.Wrapper';

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var ANIMATIONS = Object.freeze({
  FADE: 'fade',
  NONE: 'none',
  SHIFT_AWAY: 'shift_away'
});

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var PLACEMENTS = Object.freeze({
  AUTO: 'auto',
  TOP_LEFT: 'top_left',
  TOP: 'top',
  TOP_RIGHT: 'top_right',
  RIGHT_TOP: 'right_top',
  RIGHT: 'right',
  RIGHT_BOTTOM: 'right_bottom',
  BOTTOM_LEFT: 'bottom_left',
  BOTTOM: 'bottom',
  BOTTOM_RIGHT: 'bottom_right',
  LEFT_TOP: 'left_top',
  LEFT: 'left',
  LEFT_BOTTOM: 'left_bottom'
});

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var TRIGGERS = Object.freeze({
  HOVER: 'hover',
  CLICK: 'click',
  FOCUS: 'focus',
  MANUAL: 'manual'
});

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var STATES = Object.freeze({
  CLOSED: 'closed',
  CLOSING: 'closing',
  OPENING: 'opening',
  OPENED: 'opened'
});

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var getDelay = function getDelay() {
  var delayProp = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [0, 0];

  if (isNumber(delayProp) && Number.isInteger(delayProp)) {
    return {
      delayStart: delayProp,
      delayEnd: delayProp
    };
  }

  return {
    delayStart: delayProp[0],
    delayEnd: delayProp[1]
  };
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var isRelatedToElementVisible = function isRelatedToElementVisible() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      relatedTo = _ref.relatedTo;

  if (isDefinedAndNotNull(relatedTo) && isHTMLElement(relatedTo)) {
    var relatedToParent = relatedTo.parentElement;
    var percentage = getVisibilityPercentageOfElement({
      element: relatedTo,
      container: relatedToParent
    });
    return percentage > 0;
  }

  return false;
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var getTopLeftPosition = function getTopLeftPosition(_ref) {
  var linkedFloatingElement = _ref.linkedFloatingElement,
      relatedToElement = _ref.relatedToElement,
      container = _ref.container,
      withArrow = _ref.withArrow,
      withAppendTo = _ref.withAppendTo;
  var containerElement = container;
  var linkedFloatingElementId = linkedFloatingElement.dataset.linkedFloatingElementId;

  var _linkedFloatingElemen = linkedFloatingElement.getBoundingClientRect(),
      flelHeight = _linkedFloatingElemen.height;

  var arrowHeight = 0;

  if (withArrow) {
    var arrowElement = linkedFloatingElement.querySelector('.gz-linked-floating-element__arrow');
    var arrowStyles = window.getComputedStyle(arrowElement);
    var parsedArrowHeight = parseInt(arrowStyles.height, 10);
    var parsedArrowBorder = parseInt(arrowStyles.borderBottom, 10);
    arrowHeight = parsedArrowHeight + parsedArrowBorder;
  }

  var _relatedToElement$get = relatedToElement.getBoundingClientRect(),
      relatedToTop = _relatedToElement$get.top,
      relatedToLeft = _relatedToElement$get.left;

  var scrollVerticalOffset = window.scrollY;
  var scrollHorizontalOffset = window.scrollX;

  if (withAppendTo) {
    var _getRelativePositionT = getRelativePositionToParent({
      child: relatedToElement,
      parent: relatedToElement.parentElement
    });

    relatedToTop = _getRelativePositionT.top;
    relatedToLeft = _getRelativePositionT.left;
    scrollVerticalOffset = 0;
    scrollHorizontalOffset = 0;

    if (containerElement) {
      var _getRelativePositionT2 = getRelativePositionToParent({
        child: relatedToElement,
        parent: containerElement
      });

      relatedToTop = _getRelativePositionT2.top;
      relatedToLeft = _getRelativePositionT2.left;
      scrollVerticalOffset = containerElement.scrollTop;
      scrollHorizontalOffset = containerElement.scrollLeft;
    }
  }

  var nestedFloatingElement = relatedToElement.closest('[data-is-floating-element="true"]');
  var finalTop;
  var finalLeft;
  var isInsideFloatingElement = false;

  if (!nestedFloatingElement || containerElement) {
    finalTop = relatedToTop - flelHeight - arrowHeight + scrollVerticalOffset;
    finalLeft = relatedToLeft + scrollHorizontalOffset;
  } else if (nestedFloatingElement) {
    var isNestedFloatingElementDialog = !!nestedFloatingElement.dataset.isDialog;
    var nestedRelatedTo = nestedFloatingElement.querySelector("[data-related-linked-floating-element=\"".concat(linkedFloatingElementId, "\""));
    isInsideFloatingElement = true;

    if (nestedRelatedTo) {
      if (isNestedFloatingElementDialog) {
        var _getRelativePositionT3 = getRelativePositionToParent({
          child: nestedRelatedTo,
          parent: nestedFloatingElement
        }),
            nestedRelatedToTop = _getRelativePositionT3.top,
            nestedRelatedToLeft = _getRelativePositionT3.left;

        var _nestedFloatingElemen = nestedFloatingElement.getBoundingClientRect(),
            nestedFloatingElementTop = _nestedFloatingElemen.top,
            nestedFloatingElementLeft = _nestedFloatingElemen.left;

        finalTop = nestedFloatingElementTop + nestedRelatedToTop - flelHeight - arrowHeight;
        finalLeft = nestedFloatingElementLeft + nestedRelatedToLeft;
      } else {
        var _getRelativePositionT4 = getRelativePositionToParent({
          child: nestedRelatedTo,
          parent: nestedFloatingElement
        }),
            _nestedRelatedToTop = _getRelativePositionT4.top,
            _nestedRelatedToLeft = _getRelativePositionT4.left;

        finalTop = _nestedRelatedToTop - flelHeight - arrowHeight;
        finalLeft = _nestedRelatedToLeft;
      }
    }
  }

  return {
    top: finalTop,
    left: finalLeft,
    isInsideFloatingElement: isInsideFloatingElement
  };
};

function ownKeys$3(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$3(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$3(Object(source), true).forEach(function (key) { _defineProperty$3(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$3(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$3(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var getTopPosition = function getTopPosition() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      linkedFloatingElement = _ref.linkedFloatingElement,
      relatedToElement = _ref.relatedToElement,
      container = _ref.container,
      withArrow = _ref.withArrow,
      withAppendTo = _ref.withAppendTo,
      sameWidthAsChildren = _ref.sameWidthAsChildren;

  var containerElement = container;
  var linkedFloatingElementId = linkedFloatingElement.dataset.linkedFloatingElementId;

  var _linkedFloatingElemen = linkedFloatingElement.getBoundingClientRect(),
      flelHeight = _linkedFloatingElemen.height,
      flelWidth = _linkedFloatingElemen.width;

  var arrowHeight = 0;

  if (withArrow) {
    var arrowElement = linkedFloatingElement.querySelector('.gz-linked-floating-element__arrow');
    var arrowStyles = window.getComputedStyle(arrowElement);
    var parsedArrowHeight = parseInt(arrowStyles.height, 10);
    var parsedArrowBorder = parseInt(arrowStyles.borderBottom, 10);
    arrowHeight = parsedArrowHeight + parsedArrowBorder;
  }

  var _relatedToElement$get = relatedToElement.getBoundingClientRect(),
      relatedToTop = _relatedToElement$get.top,
      relatedToLeft = _relatedToElement$get.left,
      relatedToWidth = _relatedToElement$get.width;

  var scrollVerticalOffset = window.scrollY;
  var scrollHorizontalOffset = window.scrollX;

  if (withAppendTo) {
    var _getRelativePositionT = getRelativePositionToParent({
      child: relatedToElement,
      parent: relatedToElement.parentElement
    });

    relatedToTop = _getRelativePositionT.top;
    relatedToLeft = _getRelativePositionT.left;
    relatedToWidth = _getRelativePositionT.width;
    scrollVerticalOffset = 0;
    scrollHorizontalOffset = 0;

    if (containerElement) {
      var _getRelativePositionT2 = getRelativePositionToParent({
        child: relatedToElement,
        parent: containerElement
      });

      relatedToTop = _getRelativePositionT2.top;
      relatedToLeft = _getRelativePositionT2.left;
      relatedToWidth = _getRelativePositionT2.width;
      scrollVerticalOffset = containerElement.scrollTop;
      scrollHorizontalOffset = containerElement.scrollLeft;
    }
  }

  var nestedFloatingElement = relatedToElement.closest('[data-is-floating-element="true"]');
  var finalTop;
  var finalLeft;
  var finalWidth;
  var isInsideFloatingElement = false;

  if (!nestedFloatingElement || containerElement) {
    finalTop = relatedToTop - flelHeight - arrowHeight + scrollVerticalOffset;
    finalLeft = (sameWidthAsChildren ? relatedToLeft : relatedToLeft + relatedToWidth / 2 - flelWidth / 2) + scrollHorizontalOffset;
  } else if (nestedFloatingElement) {
    var isNestedFloatingElementDialog = !!nestedFloatingElement.dataset.isDialog;
    var nestedRelatedTo = nestedFloatingElement.querySelector("[data-related-linked-floating-element=\"".concat(linkedFloatingElementId, "\""));
    isInsideFloatingElement = true;

    if (nestedRelatedTo) {
      if (isNestedFloatingElementDialog) {
        var _getRelativePositionT3 = getRelativePositionToParent({
          child: nestedRelatedTo,
          parent: nestedFloatingElement
        }),
            nestedRelatedToTop = _getRelativePositionT3.top,
            nestedRelatedToLeft = _getRelativePositionT3.left,
            nestedRelatedToWidth = _getRelativePositionT3.width;

        var _nestedFloatingElemen = nestedFloatingElement.getBoundingClientRect(),
            nestedFloatingElementTop = _nestedFloatingElemen.top,
            nestedFloatingElementLeft = _nestedFloatingElemen.left;

        finalTop = nestedFloatingElementTop + nestedRelatedToTop - flelHeight - arrowHeight;
        finalLeft = sameWidthAsChildren ? nestedFloatingElementLeft + nestedRelatedToLeft : nestedFloatingElementLeft + nestedRelatedToLeft + nestedRelatedToWidth / 2 - flelWidth / 2;
      } else {
        var _getRelativePositionT4 = getRelativePositionToParent({
          child: nestedRelatedTo,
          parent: nestedFloatingElement
        }),
            _nestedRelatedToTop = _getRelativePositionT4.top,
            _nestedRelatedToLeft = _getRelativePositionT4.left,
            _nestedRelatedToWidth = _getRelativePositionT4.width;

        finalTop = _nestedRelatedToTop - flelHeight - arrowHeight;
        finalLeft = sameWidthAsChildren ? _nestedRelatedToLeft : _nestedRelatedToLeft + _nestedRelatedToWidth / 2 - flelWidth / 2;
      }
    }
  }

  if (sameWidthAsChildren) {
    finalWidth = relatedToWidth;
  }

  return _objectSpread$3(_objectSpread$3({
    top: finalTop,
    left: finalLeft
  }, finalWidth && {
    width: finalWidth
  }), {}, {
    isInsideFloatingElement: isInsideFloatingElement
  });
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var getTopRightPosition = function getTopRightPosition(_ref) {
  var linkedFloatingElement = _ref.linkedFloatingElement,
      relatedToElement = _ref.relatedToElement,
      container = _ref.container,
      withArrow = _ref.withArrow,
      withAppendTo = _ref.withAppendTo;
  var containerElement = container;
  var linkedFloatingElementId = linkedFloatingElement.dataset.linkedFloatingElementId;

  var _linkedFloatingElemen = linkedFloatingElement.getBoundingClientRect(),
      flelHeight = _linkedFloatingElemen.height,
      flelWidth = _linkedFloatingElemen.width;

  var arrowHeight = 0;

  if (withArrow) {
    var arrowElement = linkedFloatingElement.querySelector('.gz-linked-floating-element__arrow');
    var arrowStyles = window.getComputedStyle(arrowElement);
    var parsedArrowHeight = parseInt(arrowStyles.height, 10);
    var parsedArrowBorder = parseInt(arrowStyles.borderBottom, 10);
    arrowHeight = parsedArrowHeight + parsedArrowBorder;
  }

  var _relatedToElement$get = relatedToElement.getBoundingClientRect(),
      relatedToTop = _relatedToElement$get.top,
      relatedToRight = _relatedToElement$get.right;

  var scrollVerticalOffset = window.scrollY;
  var scrollHorizontalOffset = window.scrollX;

  if (withAppendTo) {
    var _getRelativePositionT = getRelativePositionToParent({
      child: relatedToElement,
      parent: relatedToElement.parentElement
    });

    relatedToTop = _getRelativePositionT.top;
    relatedToRight = _getRelativePositionT.right;
    scrollVerticalOffset = 0;
    scrollHorizontalOffset = 0;

    if (containerElement) {
      var _getRelativePositionT2 = getRelativePositionToParent({
        child: relatedToElement,
        parent: containerElement
      });

      relatedToTop = _getRelativePositionT2.top;
      relatedToRight = _getRelativePositionT2.right;
      scrollVerticalOffset = containerElement.scrollTop;
      scrollHorizontalOffset = containerElement.scrollLeft;
    }
  }

  var nestedFloatingElement = relatedToElement.closest('[data-is-floating-element="true"]');
  var finalTop;
  var finalLeft;
  var isInsideFloatingElement = false;

  if (!nestedFloatingElement || containerElement) {
    finalTop = relatedToTop - flelHeight - arrowHeight + scrollVerticalOffset;
    finalLeft = relatedToRight - flelWidth + scrollHorizontalOffset;
  } else if (nestedFloatingElement) {
    var isNestedFloatingElementDialog = !!nestedFloatingElement.dataset.isDialog;
    var nestedRelatedTo = nestedFloatingElement.querySelector("[data-related-linked-floating-element=\"".concat(linkedFloatingElementId, "\""));
    isInsideFloatingElement = true;

    if (nestedRelatedTo) {
      if (isNestedFloatingElementDialog) {
        var _getRelativePositionT3 = getRelativePositionToParent({
          child: nestedRelatedTo,
          parent: nestedFloatingElement
        }),
            nestedRelatedToTop = _getRelativePositionT3.top,
            nestedRelatedToLeft = _getRelativePositionT3.left,
            nestedRelatedToWidth = _getRelativePositionT3.width;

        var _nestedFloatingElemen = nestedFloatingElement.getBoundingClientRect(),
            nestedFloatingElementTop = _nestedFloatingElemen.top,
            nestedFloatingElementLeft = _nestedFloatingElemen.left;

        finalTop = nestedFloatingElementTop + nestedRelatedToTop - flelHeight - arrowHeight;
        finalLeft = nestedFloatingElementLeft + nestedRelatedToLeft + Math.abs(nestedRelatedToWidth - flelWidth);
      } else {
        var _getRelativePositionT4 = getRelativePositionToParent({
          child: nestedRelatedTo,
          parent: nestedFloatingElement
        }),
            _nestedRelatedToTop = _getRelativePositionT4.top,
            _nestedRelatedToLeft = _getRelativePositionT4.left,
            _nestedRelatedToWidth = _getRelativePositionT4.width;

        finalTop = _nestedRelatedToTop - flelHeight - arrowHeight;
        finalLeft = _nestedRelatedToLeft + _nestedRelatedToWidth - flelWidth;
      }
    }
  }

  return {
    top: finalTop,
    left: finalLeft,
    isInsideFloatingElement: isInsideFloatingElement
  };
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var getRightTopPosition = function getRightTopPosition() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      linkedFloatingElement = _ref.linkedFloatingElement,
      relatedToElement = _ref.relatedToElement,
      container = _ref.container,
      withArrow = _ref.withArrow,
      withAppendTo = _ref.withAppendTo;

  var containerElement = container;
  var linkedFloatingElementId = linkedFloatingElement.dataset.linkedFloatingElementId;

  var _relatedToElement$get = relatedToElement.getBoundingClientRect(),
      relatedToTop = _relatedToElement$get.top,
      relatedToRight = _relatedToElement$get.right;

  var scrollVerticalOffset = window.scrollY;
  var scrollHorizontalOffset = window.scrollX;

  if (withAppendTo) {
    var _getRelativePositionT = getRelativePositionToParent({
      child: relatedToElement,
      parent: relatedToElement.parentElement
    });

    relatedToTop = _getRelativePositionT.top;
    relatedToRight = _getRelativePositionT.right;
    scrollVerticalOffset = 0;
    scrollHorizontalOffset = 0;

    if (containerElement) {
      var _getRelativePositionT2 = getRelativePositionToParent({
        child: relatedToElement,
        parent: containerElement
      });

      relatedToTop = _getRelativePositionT2.top;
      relatedToRight = _getRelativePositionT2.right;
      scrollVerticalOffset = containerElement.scrollTop;
      scrollHorizontalOffset = containerElement.scrollLeft;
    }
  }

  var arrowHeight = 0;

  if (withArrow) {
    var arrowElement = linkedFloatingElement.querySelector('.gz-linked-floating-element__arrow');
    var arrowStyles = window.getComputedStyle(arrowElement);
    var parsedArrowWidth = parseInt(arrowStyles.width, 10);
    var parsedArrowBorder = parseInt(arrowStyles.borderTop, 10);
    arrowHeight = parsedArrowWidth + parsedArrowBorder;
  }

  var nestedFloatingElement = relatedToElement.closest('[data-is-floating-element="true"]');
  var finalTop;
  var finalLeft;
  var isInsideFloatingElement = false;

  if (!nestedFloatingElement || containerElement) {
    finalTop = relatedToTop + scrollVerticalOffset;
    finalLeft = relatedToRight + arrowHeight + scrollHorizontalOffset;
  } else if (nestedFloatingElement) {
    var isNestedFloatingElementDialog = !!nestedFloatingElement.dataset.isDialog;
    var nestedRelatedTo = nestedFloatingElement.querySelector("[data-related-linked-floating-element=\"".concat(linkedFloatingElementId, "\""));
    isInsideFloatingElement = true;

    if (nestedRelatedTo) {
      if (isNestedFloatingElementDialog) {
        var _getRelativePositionT3 = getRelativePositionToParent({
          child: nestedRelatedTo,
          parent: nestedFloatingElement
        }),
            nestedTop = _getRelativePositionT3.top,
            nestedLeft = _getRelativePositionT3.left,
            nestedWith = _getRelativePositionT3.width;

        var _nestedFloatingElemen = nestedFloatingElement.getBoundingClientRect(),
            nestedFloatingElementTop = _nestedFloatingElemen.top,
            nestedFloatingElementLeft = _nestedFloatingElemen.left;

        finalTop = nestedFloatingElementTop + nestedTop;
        finalLeft = nestedFloatingElementLeft + nestedLeft + nestedWith + arrowHeight;
      } else {
        var _getRelativePositionT4 = getRelativePositionToParent({
          child: nestedRelatedTo,
          parent: nestedFloatingElement
        }),
            _nestedTop = _getRelativePositionT4.top,
            _nestedLeft = _getRelativePositionT4.left,
            _nestedWith = _getRelativePositionT4.width;

        finalTop = _nestedTop;
        finalLeft = _nestedLeft + _nestedWith + arrowHeight;
      }
    }
  }

  return {
    top: finalTop,
    left: finalLeft,
    isInsideFloatingElement: isInsideFloatingElement
  };
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var getRightPosition = function getRightPosition() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      linkedFloatingElement = _ref.linkedFloatingElement,
      relatedToElement = _ref.relatedToElement,
      container = _ref.container,
      withArrow = _ref.withArrow,
      withAppendTo = _ref.withAppendTo;

  var containerElement = container;
  var linkedFloatingElementId = linkedFloatingElement.dataset.linkedFloatingElementId;

  var _linkedFloatingElemen = linkedFloatingElement.getBoundingClientRect(),
      flelHeight = _linkedFloatingElemen.height;

  var arrowHeight = 0;

  if (withArrow) {
    var arrowElement = linkedFloatingElement.querySelector('.gz-linked-floating-element__arrow');
    var arrowStyles = window.getComputedStyle(arrowElement);
    var parsedArrowWidth = parseInt(arrowStyles.width, 10);
    var parsedArrowBorder = parseInt(arrowStyles.borderTop, 10);
    arrowHeight = parsedArrowWidth + parsedArrowBorder;
  }

  var _relatedToElement$get = relatedToElement.getBoundingClientRect(),
      relatedToTop = _relatedToElement$get.top,
      relatedToLeft = _relatedToElement$get.left,
      relatedToHeight = _relatedToElement$get.height,
      relatedToWidth = _relatedToElement$get.width;

  var scrollVerticalOffset = window.scrollY;
  var scrollHorizontalOffset = window.scrollX;

  if (withAppendTo) {
    var _getRelativePositionT = getRelativePositionToParent({
      child: relatedToElement,
      parent: relatedToElement.parentElement
    });

    relatedToTop = _getRelativePositionT.top;
    relatedToLeft = _getRelativePositionT.left;
    relatedToHeight = _getRelativePositionT.height;
    relatedToWidth = _getRelativePositionT.width;
    scrollVerticalOffset = 0;
    scrollHorizontalOffset = 0;

    if (containerElement) {
      var _getRelativePositionT2 = getRelativePositionToParent({
        child: relatedToElement,
        parent: containerElement
      });

      relatedToTop = _getRelativePositionT2.top;
      relatedToLeft = _getRelativePositionT2.left;
      relatedToHeight = _getRelativePositionT2.height;
      relatedToWidth = _getRelativePositionT2.width;
      scrollVerticalOffset = containerElement.scrollTop;
      scrollHorizontalOffset = containerElement.scrollLeft;
    }
  }

  var nestedFloatingElement = relatedToElement.closest('[data-is-floating-element="true"]');
  var finalTop;
  var finalLeft;
  var isInsideFloatingElement = false;

  if (!nestedFloatingElement || containerElement) {
    finalTop = relatedToTop + relatedToHeight / 2 - flelHeight / 2 + scrollVerticalOffset;
    finalLeft = relatedToLeft + relatedToWidth + arrowHeight + scrollHorizontalOffset;
  } else if (nestedFloatingElement) {
    var isNestedFloatingElementDialog = !!nestedFloatingElement.dataset.isDialog;
    var nestedRelatedTo = nestedFloatingElement.querySelector("[data-related-linked-floating-element=\"".concat(linkedFloatingElementId, "\""));
    isInsideFloatingElement = true;

    if (nestedRelatedTo) {
      if (isNestedFloatingElementDialog) {
        var _getRelativePositionT3 = getRelativePositionToParent({
          child: nestedRelatedTo,
          parent: nestedFloatingElement
        }),
            nestedTop = _getRelativePositionT3.top,
            nestedLeft = _getRelativePositionT3.left,
            nestedWith = _getRelativePositionT3.width,
            nestedHeight = _getRelativePositionT3.height;

        var _nestedFloatingElemen = nestedFloatingElement.getBoundingClientRect(),
            nestedFloatingElementTop = _nestedFloatingElemen.top,
            nestedFloatingElementLeft = _nestedFloatingElemen.left;

        finalTop = nestedFloatingElementTop + nestedTop + nestedHeight / 2 - flelHeight / 2;
        finalLeft = nestedFloatingElementLeft + nestedLeft + nestedWith + arrowHeight;
      } else {
        var _getRelativePositionT4 = getRelativePositionToParent({
          child: nestedRelatedTo,
          parent: nestedFloatingElement
        }),
            _nestedTop = _getRelativePositionT4.top,
            _nestedLeft = _getRelativePositionT4.left,
            _nestedWith = _getRelativePositionT4.width,
            _nestedHeight = _getRelativePositionT4.height;

        finalTop = _nestedTop + _nestedHeight / 2 - flelHeight / 2;
        finalLeft = _nestedLeft + _nestedWith + arrowHeight;
      }
    }
  }

  return {
    top: finalTop,
    left: finalLeft,
    isInsideFloatingElement: isInsideFloatingElement
  };
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var getRightBottomPosition = function getRightBottomPosition() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      linkedFloatingElement = _ref.linkedFloatingElement,
      relatedToElement = _ref.relatedToElement,
      container = _ref.container,
      withArrow = _ref.withArrow,
      withAppendTo = _ref.withAppendTo;

  var containerElement = container;
  var linkedFloatingElementId = linkedFloatingElement.dataset.linkedFloatingElementId;

  var _linkedFloatingElemen = linkedFloatingElement.getBoundingClientRect(),
      flelHeight = _linkedFloatingElemen.height;

  var arrowHeight = 0;

  if (withArrow) {
    var arrowElement = linkedFloatingElement.querySelector('.gz-linked-floating-element__arrow');
    var arrowStyles = window.getComputedStyle(arrowElement);
    var parsedArrowWidth = parseInt(arrowStyles.width, 10);
    var parsedArrowBorder = parseInt(arrowStyles.borderTop, 10);
    arrowHeight = parsedArrowWidth + parsedArrowBorder;
  }

  var _relatedToElement$get = relatedToElement.getBoundingClientRect(),
      relatedToBottom = _relatedToElement$get.bottom,
      relatedToRight = _relatedToElement$get.right;

  var scrollVerticalOffset = window.scrollY;
  var scrollHorizontalOffset = window.scrollX;

  if (withAppendTo) {
    var _getRelativePositionT = getRelativePositionToParent({
      child: relatedToElement,
      parent: relatedToElement.parentElement
    });

    relatedToBottom = _getRelativePositionT.bottom;
    relatedToRight = _getRelativePositionT.right;
    scrollVerticalOffset = 0;
    scrollHorizontalOffset = 0;

    if (containerElement) {
      var _getRelativePositionT2 = getRelativePositionToParent({
        child: relatedToElement,
        parent: containerElement
      });

      relatedToBottom = _getRelativePositionT2.bottom;
      relatedToRight = _getRelativePositionT2.right;
      scrollVerticalOffset = containerElement.scrollTop;
      scrollHorizontalOffset = containerElement.scrollLeft;
    }
  }

  var nestedFloatingElement = relatedToElement.closest('[data-is-floating-element="true"]');
  var finalTop;
  var finalLeft;
  var isInsideFloatingElement = false;

  if (!nestedFloatingElement || containerElement) {
    finalTop = relatedToBottom - flelHeight + scrollVerticalOffset;
    finalLeft = relatedToRight + arrowHeight + scrollHorizontalOffset;
  } else if (nestedFloatingElement) {
    var isNestedFloatingElementDialog = !!nestedFloatingElement.dataset.isDialog;
    var nestedRelatedTo = nestedFloatingElement.querySelector("[data-related-linked-floating-element=\"".concat(linkedFloatingElementId, "\""));
    isInsideFloatingElement = true;

    if (nestedRelatedTo) {
      if (isNestedFloatingElementDialog) {
        var _getRelativePositionT3 = getRelativePositionToParent({
          child: nestedRelatedTo,
          parent: nestedFloatingElement
        }),
            nestedTop = _getRelativePositionT3.top,
            nestedLeft = _getRelativePositionT3.left,
            nestedWith = _getRelativePositionT3.width,
            nestedHeight = _getRelativePositionT3.height;

        var _nestedFloatingElemen = nestedFloatingElement.getBoundingClientRect(),
            nestedFloatingElementTop = _nestedFloatingElemen.top,
            nestedFloatingElementLeft = _nestedFloatingElemen.left;

        finalTop = nestedFloatingElementTop + nestedTop - Math.abs(nestedHeight - flelHeight);
        finalLeft = nestedFloatingElementLeft + nestedLeft + nestedWith + arrowHeight;
      } else {
        var _getRelativePositionT4 = getRelativePositionToParent({
          child: nestedRelatedTo,
          parent: nestedFloatingElement
        }),
            _nestedTop = _getRelativePositionT4.top,
            _nestedLeft = _getRelativePositionT4.left,
            _nestedWith = _getRelativePositionT4.width,
            _nestedHeight = _getRelativePositionT4.height;

        finalTop = _nestedTop - Math.abs(_nestedHeight - flelHeight);
        finalLeft = _nestedLeft + _nestedWith + arrowHeight;
      }
    }
  }

  return {
    top: finalTop,
    left: finalLeft,
    isInsideFloatingElement: isInsideFloatingElement
  };
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var getBottomLeftPosition = function getBottomLeftPosition() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      linkedFloatingElement = _ref.linkedFloatingElement,
      relatedToElement = _ref.relatedToElement,
      container = _ref.container,
      withArrow = _ref.withArrow,
      withAppendTo = _ref.withAppendTo;

  var containerElement = container;
  var linkedFloatingElementId = linkedFloatingElement.dataset.linkedFloatingElementId;
  var arrowHeight = 0;

  if (withArrow) {
    var arrowElement = linkedFloatingElement.querySelector('.gz-linked-floating-element__arrow');
    var arrowStyles = window.getComputedStyle(arrowElement);
    var parsedArrowHeight = parseInt(arrowStyles.height, 10);
    var parsedArrowBorder = parseInt(arrowStyles.borderTop, 10);
    arrowHeight = parsedArrowHeight + parsedArrowBorder;
  }

  var _relatedToElement$get = relatedToElement.getBoundingClientRect(),
      relatedToBottom = _relatedToElement$get.bottom,
      relatedToLeft = _relatedToElement$get.left;

  var scrollVerticalOffset = window.scrollY;
  var scrollHorizontalOffset = window.scrollX;

  if (withAppendTo) {
    var _getRelativePositionT = getRelativePositionToParent({
      child: relatedToElement,
      parent: relatedToElement.parentElement
    });

    relatedToBottom = _getRelativePositionT.bottom;
    relatedToLeft = _getRelativePositionT.left;
    scrollVerticalOffset = 0;
    scrollHorizontalOffset = 0;

    if (containerElement) {
      var _getRelativePositionT2 = getRelativePositionToParent({
        child: relatedToElement,
        parent: containerElement
      });

      relatedToBottom = _getRelativePositionT2.bottom;
      relatedToLeft = _getRelativePositionT2.left;
      scrollVerticalOffset = containerElement.scrollTop;
      scrollHorizontalOffset = containerElement.scrollLeft;
    }
  }

  var nestedFloatingElement = relatedToElement.closest('[data-is-floating-element="true"]');
  var finalTop;
  var finalLeft;
  var isInsideFloatingElement = false;

  if (!nestedFloatingElement || containerElement) {
    finalTop = relatedToBottom + arrowHeight + scrollVerticalOffset;
    finalLeft = relatedToLeft + scrollHorizontalOffset;
  } else if (nestedFloatingElement) {
    var isNestedFloatingElementDialog = !!nestedFloatingElement.dataset.isDialog;
    var nestedRelatedTo = nestedFloatingElement.querySelector("[data-related-linked-floating-element=\"".concat(linkedFloatingElementId, "\""));
    isInsideFloatingElement = true;

    if (nestedRelatedTo) {
      if (isNestedFloatingElementDialog) {
        var _getRelativePositionT3 = getRelativePositionToParent({
          child: nestedRelatedTo,
          parent: nestedFloatingElement
        }),
            nestedTop = _getRelativePositionT3.top,
            nestedLeft = _getRelativePositionT3.left,
            nestedHeight = _getRelativePositionT3.height;

        var _nestedFloatingElemen = nestedFloatingElement.getBoundingClientRect(),
            nestedFloatingElementTop = _nestedFloatingElemen.top,
            nestedFloatingElementLeft = _nestedFloatingElemen.left;

        finalTop = nestedFloatingElementTop + nestedTop + nestedHeight + arrowHeight;
        finalLeft = nestedFloatingElementLeft + nestedLeft;
      } else {
        var _getRelativePositionT4 = getRelativePositionToParent({
          child: nestedRelatedTo,
          parent: nestedFloatingElement
        }),
            _nestedTop = _getRelativePositionT4.top,
            _nestedLeft = _getRelativePositionT4.left,
            _nestedHeight = _getRelativePositionT4.height;

        finalTop = _nestedTop + _nestedHeight + arrowHeight;
        finalLeft = _nestedLeft;
      }
    }
  }

  return {
    top: finalTop,
    left: finalLeft,
    isInsideFloatingElement: isInsideFloatingElement
  };
};

function ownKeys$2(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$2(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$2(Object(source), true).forEach(function (key) { _defineProperty$2(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$2(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$2(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var getBottomPosition = function getBottomPosition() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      linkedFloatingElement = _ref.linkedFloatingElement,
      relatedToElement = _ref.relatedToElement,
      container = _ref.container,
      withArrow = _ref.withArrow,
      withAppendTo = _ref.withAppendTo,
      sameWidthAsChildren = _ref.sameWidthAsChildren;

  var containerElement = container;
  var linkedFloatingElementId = linkedFloatingElement.dataset.linkedFloatingElementId;

  var _linkedFloatingElemen = linkedFloatingElement.getBoundingClientRect(),
      flelWidth = _linkedFloatingElemen.width;

  var arrowHeight = 0;

  if (withArrow) {
    var arrowElement = linkedFloatingElement.querySelector('.gz-linked-floating-element__arrow');
    var arrowStyles = window.getComputedStyle(arrowElement);
    var parsedArrowHeight = parseInt(arrowStyles.height, 10);
    var parsedArrowBorder = parseInt(arrowStyles.borderTop, 10);
    arrowHeight = parsedArrowHeight + parsedArrowBorder;
  }

  var _relatedToElement$get = relatedToElement.getBoundingClientRect(),
      relatedToBottom = _relatedToElement$get.bottom,
      relatedToLeft = _relatedToElement$get.left,
      relatedToWidth = _relatedToElement$get.width;

  var scrollVerticalOffset = window.scrollY;
  var scrollHorizontalOffset = window.scrollX;

  if (withAppendTo) {
    var _getRelativePositionT = getRelativePositionToParent({
      child: relatedToElement,
      parent: relatedToElement.parentElement
    });

    relatedToBottom = _getRelativePositionT.bottom;
    relatedToLeft = _getRelativePositionT.left;
    relatedToWidth = _getRelativePositionT.width;
    scrollVerticalOffset = 0;
    scrollHorizontalOffset = 0;

    if (containerElement) {
      var _getRelativePositionT2 = getRelativePositionToParent({
        child: relatedToElement,
        parent: containerElement
      });

      relatedToBottom = _getRelativePositionT2.bottom;
      relatedToLeft = _getRelativePositionT2.left;
      relatedToWidth = _getRelativePositionT2.width;
      scrollVerticalOffset = containerElement.scrollTop;
      scrollHorizontalOffset = containerElement.scrollLeft;
    }
  }

  var nestedFloatingElement = relatedToElement.closest('[data-is-floating-element="true"]');
  var finalTop;
  var finalLeft;
  var finalWidth;
  var isInsideFloatingElement = false;

  if (!nestedFloatingElement || containerElement) {
    finalTop = relatedToBottom + arrowHeight + scrollVerticalOffset;
    finalLeft = (sameWidthAsChildren ? relatedToLeft : relatedToLeft + relatedToWidth / 2 - flelWidth / 2) + scrollHorizontalOffset;
  } else if (nestedFloatingElement) {
    var isNestedFloatingElementDialog = !!nestedFloatingElement.dataset.isDialog;
    var nestedRelatedTo = nestedFloatingElement.querySelector("[data-related-linked-floating-element=\"".concat(linkedFloatingElementId, "\""));
    isInsideFloatingElement = true;

    if (nestedRelatedTo) {
      if (isNestedFloatingElementDialog) {
        var _getRelativePositionT3 = getRelativePositionToParent({
          child: nestedRelatedTo,
          parent: nestedFloatingElement
        }),
            nestedTop = _getRelativePositionT3.top,
            nestedLeft = _getRelativePositionT3.left,
            nestedHeight = _getRelativePositionT3.height,
            nestedWith = _getRelativePositionT3.width;

        var _nestedFloatingElemen = nestedFloatingElement.getBoundingClientRect(),
            nestedFloatingElementTop = _nestedFloatingElemen.top,
            nestedFloatingElementLeft = _nestedFloatingElemen.left;

        finalTop = nestedFloatingElementTop + nestedTop + nestedHeight + arrowHeight;
        finalLeft = sameWidthAsChildren ? nestedFloatingElementLeft + nestedLeft : nestedFloatingElementLeft + nestedLeft + nestedWith / 2 - flelWidth / 2;
      } else {
        var _getRelativePositionT4 = getRelativePositionToParent({
          child: nestedRelatedTo,
          parent: nestedFloatingElement
        }),
            _nestedTop = _getRelativePositionT4.top,
            _nestedLeft = _getRelativePositionT4.left,
            _nestedHeight = _getRelativePositionT4.height,
            _nestedWith = _getRelativePositionT4.width;

        finalTop = _nestedTop + _nestedHeight + arrowHeight;
        finalLeft = sameWidthAsChildren ? _nestedLeft : _nestedLeft + _nestedWith / 2 - flelWidth / 2;
      }
    }
  }

  if (sameWidthAsChildren) {
    finalWidth = relatedToWidth;
  }

  return _objectSpread$2(_objectSpread$2({
    top: finalTop,
    left: finalLeft
  }, finalWidth && {
    width: finalWidth
  }), {}, {
    isInsideFloatingElement: isInsideFloatingElement
  });
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var getBottomRightPosition = function getBottomRightPosition() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      linkedFloatingElement = _ref.linkedFloatingElement,
      relatedToElement = _ref.relatedToElement,
      container = _ref.container,
      withArrow = _ref.withArrow,
      withAppendTo = _ref.withAppendTo;

  var containerElement = container;
  var linkedFloatingElementId = linkedFloatingElement.dataset.linkedFloatingElementId;

  var _linkedFloatingElemen = linkedFloatingElement.getBoundingClientRect(),
      flelWidth = _linkedFloatingElemen.width;

  var arrowHeight = 0;

  if (withArrow) {
    var arrowElement = linkedFloatingElement.querySelector('.gz-linked-floating-element__arrow');
    var arrowStyles = window.getComputedStyle(arrowElement);
    var parsedArrowHeight = parseInt(arrowStyles.height, 10);
    var parsedArrowBorder = parseInt(arrowStyles.borderTop, 10);
    arrowHeight = parsedArrowHeight + parsedArrowBorder;
  }

  var _relatedToElement$get = relatedToElement.getBoundingClientRect(),
      relatedToBottom = _relatedToElement$get.bottom,
      relatedToRight = _relatedToElement$get.right;

  var scrollVerticalOffset = window.scrollY;
  var scrollHorizontalOffset = window.scrollX;

  if (withAppendTo) {
    var _getRelativePositionT = getRelativePositionToParent({
      child: relatedToElement,
      parent: relatedToElement.parentElement
    });

    relatedToBottom = _getRelativePositionT.bottom;
    relatedToRight = _getRelativePositionT.right;
    scrollVerticalOffset = 0;
    scrollHorizontalOffset = 0;

    if (containerElement) {
      var _getRelativePositionT2 = getRelativePositionToParent({
        child: relatedToElement,
        parent: containerElement
      });

      relatedToBottom = _getRelativePositionT2.bottom;
      relatedToRight = _getRelativePositionT2.right;
      scrollVerticalOffset = containerElement.scrollTop;
      scrollHorizontalOffset = containerElement.scrollLeft;
    }
  }

  var nestedFloatingElement = relatedToElement.closest('[data-is-floating-element="true"]');
  var finalTop;
  var finalLeft;
  var isInsideFloatingElement = false;

  if (!nestedFloatingElement || containerElement) {
    finalTop = relatedToBottom + arrowHeight + scrollVerticalOffset;
    finalLeft = relatedToRight - flelWidth + scrollHorizontalOffset;
  } else if (nestedFloatingElement) {
    var isNestedFloatingElementDialog = !!nestedFloatingElement.dataset.isDialog;
    var nestedRelatedTo = nestedFloatingElement.querySelector("[data-related-linked-floating-element=\"".concat(linkedFloatingElementId, "\""));
    isInsideFloatingElement = true;

    if (nestedRelatedTo) {
      if (isNestedFloatingElementDialog) {
        var _getRelativePositionT3 = getRelativePositionToParent({
          child: nestedRelatedTo,
          parent: nestedFloatingElement
        }),
            nestedTop = _getRelativePositionT3.top,
            nestedLeft = _getRelativePositionT3.left,
            nestedWith = _getRelativePositionT3.width,
            nestedHeight = _getRelativePositionT3.height;

        var _nestedFloatingElemen = nestedFloatingElement.getBoundingClientRect(),
            nestedFloatingElementTop = _nestedFloatingElemen.top,
            nestedFloatingElementLeft = _nestedFloatingElemen.left;

        finalTop = nestedFloatingElementTop + nestedTop + nestedHeight + arrowHeight;
        finalLeft = nestedFloatingElementLeft + nestedLeft + Math.abs(nestedWith - flelWidth);
      } else {
        var _getRelativePositionT4 = getRelativePositionToParent({
          child: nestedRelatedTo,
          parent: nestedFloatingElement
        }),
            _nestedTop = _getRelativePositionT4.top,
            _nestedLeft = _getRelativePositionT4.left,
            _nestedWith = _getRelativePositionT4.width,
            _nestedHeight = _getRelativePositionT4.height;

        finalTop = _nestedTop + _nestedHeight + arrowHeight;
        finalLeft = _nestedLeft + _nestedWith - flelWidth;
      }
    }
  }

  return {
    top: finalTop,
    left: finalLeft,
    isInsideFloatingElement: isInsideFloatingElement
  };
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var getLeftTopPosition = function getLeftTopPosition() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      linkedFloatingElement = _ref.linkedFloatingElement,
      relatedToElement = _ref.relatedToElement,
      container = _ref.container,
      withArrow = _ref.withArrow,
      withAppendTo = _ref.withAppendTo;

  var containerElement = container;
  var linkedFloatingElementId = linkedFloatingElement.dataset.linkedFloatingElementId;

  var _linkedFloatingElemen = linkedFloatingElement.getBoundingClientRect(),
      flelWidth = _linkedFloatingElemen.width;

  var arrowHeight = 0;

  if (withArrow) {
    var arrowElement = linkedFloatingElement.querySelector('.gz-linked-floating-element__arrow');
    var arrowStyles = window.getComputedStyle(arrowElement);
    var parsedArrowWidth = parseInt(arrowStyles.width, 10);
    var parsedArrowBorder = parseInt(arrowStyles.borderTop, 10);
    arrowHeight = parsedArrowWidth + parsedArrowBorder;
  }

  var _relatedToElement$get = relatedToElement.getBoundingClientRect(),
      relatedToTop = _relatedToElement$get.top,
      relatedToLeft = _relatedToElement$get.left;

  var scrollVerticalOffset = window.scrollY;
  var scrollHorizontalOffset = window.scrollX;

  if (withAppendTo) {
    var _getRelativePositionT = getRelativePositionToParent({
      child: relatedToElement,
      parent: relatedToElement.parentElement
    });

    relatedToTop = _getRelativePositionT.top;
    relatedToLeft = _getRelativePositionT.left;
    scrollVerticalOffset = 0;
    scrollHorizontalOffset = 0;

    if (containerElement) {
      var _getRelativePositionT2 = getRelativePositionToParent({
        child: relatedToElement,
        parent: containerElement
      });

      relatedToTop = _getRelativePositionT2.top;
      relatedToLeft = _getRelativePositionT2.left;
      scrollVerticalOffset = containerElement.scrollTop;
      scrollHorizontalOffset = containerElement.scrollLeft;
    }
  }

  var nestedFloatingElement = relatedToElement.closest('[data-is-floating-element="true"]');
  var finalTop;
  var finalLeft;
  var isInsideFloatingElement = false;

  if (!nestedFloatingElement || containerElement) {
    finalTop = relatedToTop + scrollVerticalOffset;
    finalLeft = relatedToLeft - arrowHeight - flelWidth + scrollHorizontalOffset;
  } else if (nestedFloatingElement) {
    var isNestedFloatingElementDialog = !!nestedFloatingElement.dataset.isDialog;
    var nestedRelatedTo = nestedFloatingElement.querySelector("[data-related-linked-floating-element=\"".concat(linkedFloatingElementId, "\""));
    isInsideFloatingElement = true;

    if (nestedRelatedTo) {
      if (isNestedFloatingElementDialog) {
        var _getRelativePositionT3 = getRelativePositionToParent({
          child: nestedRelatedTo,
          parent: nestedFloatingElement
        }),
            nestedTop = _getRelativePositionT3.top,
            nestedLeft = _getRelativePositionT3.left;

        var _nestedFloatingElemen = nestedFloatingElement.getBoundingClientRect(),
            nestedFloatingElementTop = _nestedFloatingElemen.top,
            nestedFloatingElementLeft = _nestedFloatingElemen.left;

        finalTop = nestedFloatingElementTop + nestedTop;
        finalLeft = nestedFloatingElementLeft + nestedLeft - flelWidth - arrowHeight;
      } else {
        var _getRelativePositionT4 = getRelativePositionToParent({
          child: nestedRelatedTo,
          parent: nestedFloatingElement
        }),
            _nestedTop = _getRelativePositionT4.top,
            _nestedLeft = _getRelativePositionT4.left;

        finalTop = _nestedTop;
        finalLeft = _nestedLeft - flelWidth - arrowHeight;
      }
    }
  }

  return {
    top: finalTop,
    left: finalLeft,
    isInsideFloatingElement: isInsideFloatingElement
  };
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var getLeftPosition = function getLeftPosition() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      linkedFloatingElement = _ref.linkedFloatingElement,
      relatedToElement = _ref.relatedToElement,
      container = _ref.container,
      withArrow = _ref.withArrow,
      withAppendTo = _ref.withAppendTo;

  var containerElement = container;
  var linkedFloatingElementId = linkedFloatingElement.dataset.linkedFloatingElementId;

  var _linkedFloatingElemen = linkedFloatingElement.getBoundingClientRect(),
      flelHeight = _linkedFloatingElemen.height,
      flelWidth = _linkedFloatingElemen.width;

  var arrowHeight = 0;

  if (withArrow) {
    var arrowElement = linkedFloatingElement.querySelector('.gz-linked-floating-element__arrow');
    var arrowStyles = window.getComputedStyle(arrowElement);
    var parsedArrowWidth = parseInt(arrowStyles.width, 10);
    var parsedArrowBorder = parseInt(arrowStyles.borderTop, 10);
    arrowHeight = parsedArrowWidth + parsedArrowBorder;
  }

  var _relatedToElement$get = relatedToElement.getBoundingClientRect(),
      relatedToTop = _relatedToElement$get.top,
      relatedToLeft = _relatedToElement$get.left,
      relatedToHeight = _relatedToElement$get.height;

  var scrollVerticalOffset = window.scrollY;
  var scrollHorizontalOffset = window.scrollX;

  if (withAppendTo) {
    var _getRelativePositionT = getRelativePositionToParent({
      child: relatedToElement,
      parent: relatedToElement.parentElement
    });

    relatedToTop = _getRelativePositionT.top;
    relatedToLeft = _getRelativePositionT.left;
    relatedToHeight = _getRelativePositionT.height;
    scrollVerticalOffset = 0;
    scrollHorizontalOffset = 0;

    if (containerElement) {
      var _getRelativePositionT2 = getRelativePositionToParent({
        child: relatedToElement,
        parent: containerElement
      });

      relatedToTop = _getRelativePositionT2.top;
      relatedToLeft = _getRelativePositionT2.left;
      relatedToHeight = _getRelativePositionT2.height;
      scrollVerticalOffset = containerElement.scrollTop;
      scrollHorizontalOffset = containerElement.scrollLeft;
    }
  }

  var nestedFloatingElement = relatedToElement.closest('[data-is-floating-element="true"]');
  var finalTop;
  var finalLeft;
  var isInsideFloatingElement = false;

  if (!nestedFloatingElement || containerElement) {
    finalTop = relatedToTop + relatedToHeight / 2 - flelHeight / 2 + scrollVerticalOffset;
    finalLeft = relatedToLeft - arrowHeight - flelWidth + scrollHorizontalOffset;
  } else if (nestedFloatingElement) {
    var isNestedFloatingElementDialog = !!nestedFloatingElement.dataset.isDialog;
    var nestedRelatedTo = nestedFloatingElement.querySelector("[data-related-linked-floating-element=\"".concat(linkedFloatingElementId, "\""));
    isInsideFloatingElement = true;

    if (nestedRelatedTo) {
      if (isNestedFloatingElementDialog) {
        var _getRelativePositionT3 = getRelativePositionToParent({
          child: nestedRelatedTo,
          parent: nestedFloatingElement
        }),
            nestedTop = _getRelativePositionT3.top,
            nestedLeft = _getRelativePositionT3.left,
            nestedHeight = _getRelativePositionT3.height;

        var _nestedFloatingElemen = nestedFloatingElement.getBoundingClientRect(),
            nestedFloatingElementTop = _nestedFloatingElemen.top,
            nestedFloatingElementLeft = _nestedFloatingElemen.left;

        finalTop = nestedFloatingElementTop + nestedTop + nestedHeight / 2 - flelHeight / 2;
        finalLeft = nestedFloatingElementLeft + nestedLeft - flelWidth - arrowHeight;
      } else {
        var _getRelativePositionT4 = getRelativePositionToParent({
          child: nestedRelatedTo,
          parent: nestedFloatingElement
        }),
            _nestedTop = _getRelativePositionT4.top,
            _nestedLeft = _getRelativePositionT4.left,
            _nestedHeight = _getRelativePositionT4.height;

        finalTop = _nestedTop + _nestedHeight / 2 - flelHeight / 2;
        finalLeft = _nestedLeft - flelWidth - arrowHeight;
      }
    }
  }

  return {
    top: finalTop,
    left: finalLeft,
    isInsideFloatingElement: isInsideFloatingElement
  };
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var getLeftBottomPosition = function getLeftBottomPosition() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      linkedFloatingElement = _ref.linkedFloatingElement,
      relatedToElement = _ref.relatedToElement,
      container = _ref.container,
      withArrow = _ref.withArrow,
      withAppendTo = _ref.withAppendTo;

  var containerElement = container;
  var linkedFloatingElementId = linkedFloatingElement.dataset.linkedFloatingElementId;

  var _linkedFloatingElemen = linkedFloatingElement.getBoundingClientRect(),
      flelHeight = _linkedFloatingElemen.height,
      flelWidth = _linkedFloatingElemen.width;

  var arrowHeight = 0;

  if (withArrow) {
    var arrowElement = linkedFloatingElement.querySelector('.gz-linked-floating-element__arrow');
    var arrowStyles = window.getComputedStyle(arrowElement);
    var parsedArrowWidth = parseInt(arrowStyles.width, 10);
    var parsedArrowBorder = parseInt(arrowStyles.borderTop, 10);
    arrowHeight = parsedArrowWidth + parsedArrowBorder;
  }

  var _relatedToElement$get = relatedToElement.getBoundingClientRect(),
      relatedToBottom = _relatedToElement$get.bottom,
      relatedToLeft = _relatedToElement$get.left;

  var scrollVerticalOffset = window.scrollY;
  var scrollHorizontalOffset = window.scrollX;

  if (withAppendTo) {
    var _getRelativePositionT = getRelativePositionToParent({
      child: relatedToElement,
      parent: relatedToElement.parentElement
    });

    relatedToBottom = _getRelativePositionT.bottom;
    relatedToLeft = _getRelativePositionT.left;
    scrollVerticalOffset = 0;
    scrollHorizontalOffset = 0;

    if (containerElement) {
      var _getRelativePositionT2 = getRelativePositionToParent({
        child: relatedToElement,
        parent: containerElement
      });

      relatedToBottom = _getRelativePositionT2.bottom;
      relatedToLeft = _getRelativePositionT2.left;
      scrollVerticalOffset = containerElement.scrollTop;
      scrollHorizontalOffset = containerElement.scrollLeft;
    }
  }

  var nestedFloatingElement = relatedToElement.closest('[data-is-floating-element="true"]');
  var finalTop;
  var finalLeft;
  var isInsideFloatingElement = false;

  if (!nestedFloatingElement || containerElement) {
    finalTop = relatedToBottom - flelHeight + scrollVerticalOffset;
    finalLeft = relatedToLeft - arrowHeight - flelWidth + scrollHorizontalOffset;
  } else if (nestedFloatingElement) {
    var isNestedFloatingElementDialog = !!nestedFloatingElement.dataset.isDialog;
    var nestedRelatedTo = nestedFloatingElement.querySelector("[data-related-linked-floating-element=\"".concat(linkedFloatingElementId, "\""));
    isInsideFloatingElement = true;

    if (nestedRelatedTo) {
      if (isNestedFloatingElementDialog) {
        var _getRelativePositionT3 = getRelativePositionToParent({
          child: nestedRelatedTo,
          parent: nestedFloatingElement
        }),
            nestedTop = _getRelativePositionT3.top,
            nestedLeft = _getRelativePositionT3.left,
            nestedHeight = _getRelativePositionT3.height;

        var _nestedFloatingElemen = nestedFloatingElement.getBoundingClientRect(),
            nestedFloatingElementTop = _nestedFloatingElemen.top,
            nestedFloatingElementLeft = _nestedFloatingElemen.left;

        finalTop = nestedFloatingElementTop + nestedTop + Math.abs(flelHeight - nestedHeight);
        finalLeft = nestedFloatingElementLeft + nestedLeft - flelWidth - arrowHeight;
      } else {
        var _getRelativePositionT4 = getRelativePositionToParent({
          child: nestedRelatedTo,
          parent: nestedFloatingElement
        }),
            nestedBottom = _getRelativePositionT4.bottom,
            _nestedLeft = _getRelativePositionT4.left;

        finalTop = nestedBottom - flelHeight;
        finalLeft = _nestedLeft - flelWidth - arrowHeight;
      }
    }
  }

  return {
    top: finalTop,
    left: finalLeft,
    isInsideFloatingElement: isInsideFloatingElement
  };
};

function _toConsumableArray(arr) { return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray$1(arr) || _nonIterableSpread(); }

function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _iterableToArray(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }

function _arrayWithoutHoles(arr) { if (Array.isArray(arr)) return _arrayLikeToArray$1(arr); }

function _slicedToArray$1(arr, i) { return _arrayWithHoles$1(arr) || _iterableToArrayLimit$1(arr, i) || _unsupportedIterableToArray$1(arr, i) || _nonIterableRest$1(); }

function _nonIterableRest$1() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray$1(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$1(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$1(o, minLen); }

function _arrayLikeToArray$1(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit$1(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles$1(arr) { if (Array.isArray(arr)) return arr; }

var _fitsInsideViewportOrContainer = function _fitsInsideViewportOrContainer() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      linkedFloatingElement = _ref.linkedFloatingElement,
      relatedToElement = _ref.relatedToElement,
      _ref$container = _ref.container,
      container = _ref$container === void 0 ? null : _ref$container,
      isInsideFloatingElement = _ref.isInsideFloatingElement,
      top = _ref.top,
      left = _ref.left,
      forceViewportPlacement = _ref.forceViewportPlacement,
      withAppendTo = _ref.withAppendTo;

  var _linkedFloatingElemen = linkedFloatingElement.getBoundingClientRect(),
      flelWidth = _linkedFloatingElemen.width,
      flelHeight = _linkedFloatingElemen.height;

  if (!forceViewportPlacement && isDefinedAndNotNull(container)) {
    var _container$getBoundin = container.getBoundingClientRect(),
        containerTop = _container$getBoundin.top,
        containerLeft = _container$getBoundin.left,
        containerWidth = _container$getBoundin.width,
        containerHeight = _container$getBoundin.height;

    if (withAppendTo) {
      var scrollTopOffset = container.scrollTop;
      var scrollLeftOffset = container.scrollLeft;
      var flelTopRelativeToContainer = top + containerTop - scrollTopOffset;
      var flelLeftRelativeToContainer = left + containerLeft - scrollLeftOffset;
      var fitsInsideContainerWithAppend = flelTopRelativeToContainer >= containerTop && flelTopRelativeToContainer + flelHeight <= containerTop + containerHeight && flelLeftRelativeToContainer >= containerLeft && flelLeftRelativeToContainer + flelWidth <= containerLeft + containerWidth;
      return fitsInsideContainerWithAppend;
    }

    var containerTopRelativeToViewport = containerTop + window.pageYOffset;
    var containerBottomRelativeToViewport = containerTop + containerHeight + window.pageYOffset;
    var containerLeftRelativeToViewport = containerLeft + window.pageXOffset;
    var containerRightRelativeToViewport = containerLeft + containerWidth + window.pageXOffset;
    var fitsInsideContainerWithoutAppend = top >= containerTopRelativeToViewport && top + flelHeight <= containerBottomRelativeToViewport && left >= containerLeftRelativeToViewport && left + flelWidth <= containerRightRelativeToViewport;
    return fitsInsideContainerWithoutAppend;
  }

  var topToCheck = top - window.scrollY;
  var leftToCheck = left - window.scrollX;

  if (isInsideFloatingElement) {
    var floatingElementOfRelatedTo = relatedToElement.closest('[data-is-floating-element="true"]');

    var _floatingElementOfRel = floatingElementOfRelatedTo.getBoundingClientRect(),
        floatingElementTop = _floatingElementOfRel.top,
        floatingElementLeft = _floatingElementOfRel.left;

    topToCheck = floatingElementTop + top;
    leftToCheck = floatingElementLeft + left;
  }

  var fitsInsideViewport = topToCheck >= 0 && topToCheck + flelHeight <= window.innerHeight && leftToCheck >= 0 && leftToCheck + flelWidth <= window.innerWidth;
  return fitsInsideViewport;
};

var _getPositionByPlacement = function _getPositionByPlacement() {
  var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      linkedFloatingElement = _ref2.linkedFloatingElement,
      relatedToElement = _ref2.relatedToElement,
      container = _ref2.container,
      placement = _ref2.placement,
      sameWidthAsChildren = _ref2.sameWidthAsChildren,
      withArrow = _ref2.withArrow,
      withAppendTo = _ref2.withAppendTo;

  if (sameWidthAsChildren) {
    var _relatedToElement$get = relatedToElement.getBoundingClientRect(),
        relatedToWidth = _relatedToElement$get.width; // eslint-disable-next-line no-param-reassign


    linkedFloatingElement.style.width = "".concat(relatedToWidth, "px");
  }

  switch (placement) {
    /* Top positions */
    case PLACEMENTS.TOP_LEFT:
      return getTopLeftPosition({
        linkedFloatingElement: linkedFloatingElement,
        relatedToElement: relatedToElement,
        container: container,
        sameWidthAsChildren: sameWidthAsChildren,
        withArrow: withArrow,
        withAppendTo: withAppendTo
      });

    case PLACEMENTS.TOP:
      return getTopPosition({
        linkedFloatingElement: linkedFloatingElement,
        relatedToElement: relatedToElement,
        container: container,
        sameWidthAsChildren: sameWidthAsChildren,
        withArrow: withArrow,
        withAppendTo: withAppendTo
      });

    case PLACEMENTS.TOP_RIGHT:
      return getTopRightPosition({
        linkedFloatingElement: linkedFloatingElement,
        relatedToElement: relatedToElement,
        container: container,
        sameWidthAsChildren: sameWidthAsChildren,
        withArrow: withArrow,
        withAppendTo: withAppendTo
      });

    /* Right positions */

    case PLACEMENTS.RIGHT_TOP:
      return getRightTopPosition({
        linkedFloatingElement: linkedFloatingElement,
        relatedToElement: relatedToElement,
        container: container,
        withArrow: withArrow,
        withAppendTo: withAppendTo
      });

    case PLACEMENTS.RIGHT:
      return getRightPosition({
        linkedFloatingElement: linkedFloatingElement,
        relatedToElement: relatedToElement,
        container: container,
        withArrow: withArrow,
        withAppendTo: withAppendTo
      });

    case PLACEMENTS.RIGHT_BOTTOM:
      return getRightBottomPosition({
        linkedFloatingElement: linkedFloatingElement,
        relatedToElement: relatedToElement,
        container: container,
        withArrow: withArrow,
        withAppendTo: withAppendTo
      });

    /* Bottom positions */

    case PLACEMENTS.BOTTOM_LEFT:
      return getBottomLeftPosition({
        linkedFloatingElement: linkedFloatingElement,
        relatedToElement: relatedToElement,
        container: container,
        sameWidthAsChildren: sameWidthAsChildren,
        withArrow: withArrow,
        withAppendTo: withAppendTo
      });

    case PLACEMENTS.BOTTOM:
      return getBottomPosition({
        linkedFloatingElement: linkedFloatingElement,
        relatedToElement: relatedToElement,
        container: container,
        sameWidthAsChildren: sameWidthAsChildren,
        withArrow: withArrow,
        withAppendTo: withAppendTo
      });

    case PLACEMENTS.BOTTOM_RIGHT:
      return getBottomRightPosition({
        linkedFloatingElement: linkedFloatingElement,
        relatedToElement: relatedToElement,
        container: container,
        sameWidthAsChildren: sameWidthAsChildren,
        withArrow: withArrow,
        withAppendTo: withAppendTo
      });

    /* Left positions */

    case PLACEMENTS.LEFT_TOP:
      return getLeftTopPosition({
        linkedFloatingElement: linkedFloatingElement,
        relatedToElement: relatedToElement,
        container: container,
        withArrow: withArrow,
        withAppendTo: withAppendTo
      });

    case PLACEMENTS.LEFT:
      return getLeftPosition({
        linkedFloatingElement: linkedFloatingElement,
        relatedToElement: relatedToElement,
        container: container,
        withArrow: withArrow,
        withAppendTo: withAppendTo
      });

    case PLACEMENTS.LEFT_BOTTOM:
      return getLeftBottomPosition({
        linkedFloatingElement: linkedFloatingElement,
        relatedToElement: relatedToElement,
        container: container,
        withArrow: withArrow,
        withAppendTo: withAppendTo
      });

    default:
      return {
        top: null,
        left: null,
        isInsideFloatingElement: false
      };
  }
};

var getPositionByPlacement = function getPositionByPlacement() {
  var _ref3 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      linkedFloatingElement = _ref3.linkedFloatingElement,
      relatedToElement = _ref3.relatedToElement,
      placement = _ref3.placement,
      _ref3$initialPlacemen = _ref3.initialPlacement,
      initialPlacement = _ref3$initialPlacemen === void 0 ? placement : _ref3$initialPlacemen,
      _ref3$container = _ref3.container,
      container = _ref3$container === void 0 ? null : _ref3$container,
      sameWidthAsChildren = _ref3.sameWidthAsChildren,
      forceViewportPlacement = _ref3.forceViewportPlacement,
      withArrow = _ref3.withArrow,
      withAppendTo = _ref3.withAppendTo,
      _ref3$placementsCheck = _ref3.placementsChecked,
      placementsChecked = _ref3$placementsCheck === void 0 ? [placement] : _ref3$placementsCheck,
      forceOpenEvenNotFit = _ref3.forceOpenEvenNotFit,
      avoidReposition = _ref3.avoidReposition;

  var newPosition = _getPositionByPlacement({
    linkedFloatingElement: linkedFloatingElement,
    relatedToElement: relatedToElement,
    container: container,
    placement: placement,
    sameWidthAsChildren: sameWidthAsChildren,
    withArrow: withArrow,
    withAppendTo: withAppendTo
  });

  newPosition.placement = placement;
  newPosition.shouldClose = false;

  if (isDefinedAndNotNull(newPosition.top)) {
    newPosition.top = floatRound(newPosition.top, 0);
  }

  if (isDefinedAndNotNull(newPosition.left)) {
    newPosition.left = floatRound(newPosition.left, 0);
  }

  if (isDefinedAndNotNull(newPosition.width)) {
    newPosition.width = floatRound(newPosition.width, 0);
  }

  if (forceViewportPlacement && !isRelatedToElementVisible({
    relatedTo: relatedToElement
  })) {
    newPosition.shouldClose = true;
  } else if (isNull(newPosition.top) || isNull(newPosition.left) || !_fitsInsideViewportOrContainer({
    linkedFloatingElement: linkedFloatingElement,
    relatedToElement: relatedToElement,
    container: container,
    isInsideFloatingElement: newPosition.isInsideFloatingElement,
    top: newPosition.top,
    left: newPosition.left,
    forceViewportPlacement: forceViewportPlacement,
    withAppendTo: withAppendTo
  })) {
    if (!avoidReposition) {
      if (placement) {
        var _placement$split = placement.split('_'),
            _placement$split2 = _slicedToArray$1(_placement$split, 1),
            placementPrefix = _placement$split2[0];

        var placementsByPrefix = Object.values(PLACEMENTS).filter(function (each) {
          return each.startsWith(placementPrefix);
        });
        var areAllPlacementsByPrefixChecked = placementsByPrefix.every(function (each) {
          return placementsChecked.includes(each);
        });

        if (!areAllPlacementsByPrefixChecked) {
          var placementByPrefixToBeChecked = placementsByPrefix.find(function (each) {
            return !placementsChecked.includes(each);
          });
          return getPositionByPlacement({
            linkedFloatingElement: linkedFloatingElement,
            relatedToElement: relatedToElement,
            placement: placementByPrefixToBeChecked,
            initialPlacement: initialPlacement,
            container: container,
            sameWidthAsChildren: sameWidthAsChildren,
            forceViewportPlacement: forceViewportPlacement,
            withArrow: withArrow,
            withAppendTo: withAppendTo,
            forceOpenEvenNotFit: forceOpenEvenNotFit,
            placementsChecked: [].concat(_toConsumableArray(placementsChecked), [placementByPrefixToBeChecked])
          });
        }
      }

      var allPlacements = Object.values(PLACEMENTS);

      if (sameWidthAsChildren) {
        allPlacements = [PLACEMENTS.TOP, PLACEMENTS.BOTTOM];
      }

      var firstPlacementNotChecked = allPlacements.find(function (each) {
        return !placementsChecked.includes(each);
      });

      if (firstPlacementNotChecked) {
        return getPositionByPlacement({
          linkedFloatingElement: linkedFloatingElement,
          relatedToElement: relatedToElement,
          placement: firstPlacementNotChecked,
          initialPlacement: initialPlacement,
          container: container,
          sameWidthAsChildren: sameWidthAsChildren,
          forceViewportPlacement: forceViewportPlacement,
          withArrow: withArrow,
          withAppendTo: withAppendTo,
          forceOpenEvenNotFit: forceOpenEvenNotFit,
          placementsChecked: [].concat(_toConsumableArray(placementsChecked), [firstPlacementNotChecked])
        });
      }
    }

    var linkedFloatingElementContent = linkedFloatingElement.querySelector('.gz-linked-floating-element__content');
    var lastPlacementVisible = linkedFloatingElementContent.dataset.lastPlacementVisible;
    var placementToUse;

    if (forceOpenEvenNotFit) {
      placementToUse = lastPlacementVisible;

      if (!placementToUse) {
        placementToUse = initialPlacement;
      }

      if (placementToUse === PLACEMENTS.AUTO) {
        var _ref4 = sameWidthAsChildren ? [PLACEMENTS.TOP, PLACEMENTS.BOTTOM] : Object.values(PLACEMENTS).filter(function (each) {
          return each !== PLACEMENTS.AUTO;
        }),
            _ref5 = _slicedToArray$1(_ref4, 1),
            firstPlacement = _ref5[0];

        placementToUse = firstPlacement;
      }
    }

    newPosition = _getPositionByPlacement({
      linkedFloatingElement: linkedFloatingElement,
      relatedToElement: relatedToElement,
      placement: placementToUse,
      sameWidthAsChildren: sameWidthAsChildren,
      withArrow: withArrow,
      withAppendTo: withAppendTo
    });
    newPosition.placement = placementToUse;
    newPosition.lastPlacementVisible = placementToUse;
    newPosition.shouldClose = false;

    if (isDefinedAndNotNull(newPosition.top)) {
      newPosition.top = floatRound(newPosition.top, 0);
    }

    if (isDefinedAndNotNull(newPosition.left)) {
      newPosition.left = floatRound(newPosition.left, 0);
    }

    if (isDefinedAndNotNull(newPosition.width)) {
      newPosition.width = floatRound(newPosition.width, 0);
    } // If linkedFloatingElement doesn't fit in the viewport and neither the opposite placement, close it.


    if (!forceOpenEvenNotFit && (isNull(newPosition.top) || isNull(newPosition.left))) {
      newPosition.shouldClose = true;
    }
  } else {
    newPosition.lastPlacementVisible = placement;
  }

  return newPosition;
};

var _excluded = ["animation", "interactive", "avoidReposition", "appendTo", "forceViewportPlacement", "className", "delay", "sameWidthAsChildren", "shouldWrapChildren", "forceOpenEvenNotFit", "forceDefaultCloseMethod", "disableBackgroundScroll", "closeOnEscape", "closeOnClickOutside", "content", "limitsPlacementContainer", "placement", "trigger", "opened", "disabled", "children", "withArrow", "onPlacementChanged", "onOpened", "onClose", "onClosed", "onClickOutside", "onEscape"];

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$1(Object(source), true).forEach(function (key) { _defineProperty$1(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$1(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
var LinkedFloatingElement$1 = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var animation = _ref.animation,
      interactive = _ref.interactive,
      avoidReposition = _ref.avoidReposition,
      appendTo = _ref.appendTo,
      forceViewportPlacement = _ref.forceViewportPlacement,
      className = _ref.className,
      delay = _ref.delay,
      sameWidthAsChildren = _ref.sameWidthAsChildren,
      shouldWrapChildren = _ref.shouldWrapChildren,
      forceOpenEvenNotFit = _ref.forceOpenEvenNotFit,
      forceDefaultCloseMethod = _ref.forceDefaultCloseMethod,
      disableBackgroundScroll = _ref.disableBackgroundScroll,
      closeOnEscape = _ref.closeOnEscape,
      closeOnClickOutside = _ref.closeOnClickOutside,
      content = _ref.content,
      limitsPlacementContainer = _ref.limitsPlacementContainer,
      placement = _ref.placement,
      trigger = _ref.trigger,
      opened = _ref.opened,
      disabled = _ref.disabled,
      children = _ref.children,
      withArrow = _ref.withArrow,
      onPlacementChanged = _ref.onPlacementChanged,
      onOpened = _ref.onOpened,
      onClose = _ref.onClose,
      onClosed = _ref.onClosed,
      onClickOutsideFromProps = _ref.onClickOutside,
      onEscapeFromProps = _ref.onEscape,
      rest = _objectWithoutProperties(_ref, _excluded);

  var dataAttributes = useDataAttributes(rest, {
    excludedAttributes: ['data-related-linked-floating-element', 'data-placement', 'data-last-placement-visible']
  });
  var contentToRender = isFunction(content) ? content : function () {
    return content;
  };
  var linkedFloatingElementId = useRef(getId());

  var _useState = useState(function () {
    return {
      isMounted: true,
      isOpened: trigger === TRIGGERS.MANUAL && opened,
      internalIsOpened: trigger === TRIGGERS.MANUAL && opened
    };
  }),
      _useState2 = _slicedToArray(_useState, 2),
      _useState2$ = _useState2[0],
      isMounted = _useState2$.isMounted,
      isOpened = _useState2$.isOpened,
      internalIsOpened = _useState2$.internalIsOpened,
      setOpenedState = _useState2[1];

  var _useState3 = useState(STATES.CLOSED),
      _useState4 = _slicedToArray(_useState3, 2),
      animationState = _useState4[0],
      setAnimationState = _useState4[1];

  var delayOpenTimeoutIdRef = useRef();
  var delayCloseTimeoutIdRef = useRef();
  var animationOpenRef = useRef(false);
  var animationCloseRef = useRef(false);
  var linkedFloatingElementRef = useRef();
  var linkedFloatingElementContentRef = useRef();
  var relatedToRef = useRef();
  var limitsPlacementContainerRef = useRef();
  var shouldWrap = isString(children) || shouldWrapChildren;
  var triggerToProcess = useMemo(function () {
    return Array.isArray(trigger) ? trigger : [trigger];
  }, [trigger]);
  var relatedTo;

  if (shouldWrap) {
    relatedTo = /*#__PURE__*/jsx(Wrapper, {
      ref: relatedToRef,
      children: children
    });
  } else {
    relatedTo = children;
  }

  var open = useCallback(function () {
    animationOpenRef.current = true; // if there is a close delay timeout in progress, cancel it

    if (delayCloseTimeoutIdRef.current) {
      clearTimeout(delayCloseTimeoutIdRef.current);
    }

    if (linkedFloatingElementRef.current) {
      // In case that we have the DOM element, it's opened and we don't need to animate it.
      linkedFloatingElementContentRef.current.dataset.state = STATES.OPENED;
      setAnimationState(STATES.OPENED);
      onOpened();
    } else if (triggerToProcess.includes(TRIGGERS.HOVER)) {
      // In case that we don't have the DOM element and the trigger is hover, we can animate with delay.
      var _getDelay = getDelay(delay),
          delayStart = _getDelay.delayStart;

      delayOpenTimeoutIdRef.current = setTimeout(function () {
        setOpenedState(function (prevState) {
          return _objectSpread$1(_objectSpread$1({}, prevState), {}, {
            isOpened: true,
            isMounted: true,
            internalIsOpened: true
          });
        });
      }, delayStart);
    } else {
      // In case that we don't have the DOM element, we can animate inmediately.
      setOpenedState(function (prevState) {
        return _objectSpread$1(_objectSpread$1({}, prevState), {}, {
          isOpened: true,
          isMounted: true,
          internalIsOpened: true
        });
      });
    }
  }, [triggerToProcess, delay, onOpened]);
  var close = useCallback(function () {
    animationOpenRef.current = false;
    animationCloseRef.current = true; // if there is a open delay timeout in progress, cancel it

    if (delayOpenTimeoutIdRef.current) {
      clearTimeout(delayOpenTimeoutIdRef.current);
    }

    if (!linkedFloatingElementRef.current) {
      // In case that we don't have the DOM element, it's closed and we don't need to animate it.
      animationCloseRef.current = false;
      onClose();
      setOpenedState(function (prevState) {
        return _objectSpread$1(_objectSpread$1({}, prevState), {}, {
          isOpened: false,
          isMounted: false,
          internalIsOpened: false
        });
      });
      setAnimationState(STATES.CLOSED);
      onClosed();
    } else if (triggerToProcess.includes(TRIGGERS.HOVER)) {
      // In case we have the DOM element and the trigger is hover, we can animate with a delay effect.
      var _getDelay2 = getDelay(delay),
          delayEnd = _getDelay2.delayEnd;

      delayCloseTimeoutIdRef.current = setTimeout(function () {
        linkedFloatingElementContentRef.current.dataset.state = STATES.CLOSING;
        setAnimationState(STATES.CLOSING);
        setOpenedState(function (prevState) {
          return _objectSpread$1(_objectSpread$1({}, prevState), {}, {
            isOpened: false
          });
        });
        onClose();
      }, delayEnd);
    } else {
      // In case that we have the DOM element, we can animate inmediately.
      setAnimationState(STATES.CLOSING);
      linkedFloatingElementContentRef.current.dataset.state = STATES.CLOSING;
      setOpenedState(function (prevState) {
        return _objectSpread$1(_objectSpread$1({}, prevState), {}, {
          isOpened: false
        });
      });
      onClose();
    }
  }, [delay, triggerToProcess, onClose, onClosed]);

  var isCursorOutsideInteractiveArea = function isCursorOutsideInteractiveArea(event) {
    var clientX = event.clientX,
        clientY = event.clientY,
        target = event.target;

    var _relatedToRef$current = relatedToRef.current.getBoundingClientRect(),
        relatedToTop = _relatedToRef$current.top,
        relatedToLeft = _relatedToRef$current.left,
        relatedToWidth = _relatedToRef$current.width,
        relatedToHeight = _relatedToRef$current.height;

    if (linkedFloatingElementRef.current) {
      var _linkedFloatingElemen = linkedFloatingElementRef.current.getBoundingClientRect(),
          linkedFloatingElementTop = _linkedFloatingElemen.top,
          linkedFloatingElementLeft = _linkedFloatingElemen.left,
          linkedFloatingElementWidth = _linkedFloatingElemen.width,
          linkedFloatingElementHeight = _linkedFloatingElemen.height;

      var currentPlacement = linkedFloatingElementContentRef.current.dataset.placement;
      var isCursorOverLinkedFloatingElement = linkedFloatingElementRef.current.contains(target); // If the cursor is over LinkedFloatingElement, the cursor is not outside the interactiveArea

      if (isCursorOverLinkedFloatingElement) {
        return false;
      }

      var exceededTop;
      var exceededBottom;
      var exceededLeft;
      var exceededRight;

      if ([PLACEMENTS.TOP, PLACEMENTS.TOP_LEFT, PLACEMENTS.TOP_RIGHT].includes(currentPlacement)) {
        exceededTop = clientY < linkedFloatingElementTop;
        exceededBottom = clientY - (relatedToTop + relatedToHeight) > 0;
        exceededLeft = relatedToLeft - clientX > 0;
        exceededRight = clientX - (relatedToLeft + relatedToWidth) > 0;
      } else if ([PLACEMENTS.BOTTOM, PLACEMENTS.BOTTOM_LEFT, PLACEMENTS.BOTTOM_RIGHT].includes(currentPlacement)) {
        exceededTop = relatedToTop - clientY > 0;
        exceededBottom = clientY > linkedFloatingElementTop + linkedFloatingElementHeight;
        exceededLeft = relatedToLeft - clientX > 0;
        exceededRight = clientX - (relatedToLeft + relatedToWidth) > 0;
      } else if ([PLACEMENTS.LEFT, PLACEMENTS.LEFT_BOTTOM, PLACEMENTS.LEFT_TOP].includes(currentPlacement)) {
        exceededTop = relatedToTop - clientY > 0;
        exceededBottom = clientY - (relatedToTop + relatedToHeight) > 0;
        exceededLeft = clientX < linkedFloatingElementLeft;
        exceededRight = clientX - (relatedToLeft + relatedToWidth) > 0;
      } else if ([PLACEMENTS.RIGHT, PLACEMENTS.RIGHT_BOTTOM, PLACEMENTS.RIGHT_TOP].includes(currentPlacement)) {
        exceededTop = relatedToTop - clientY > 0;
        exceededBottom = clientY - (relatedToTop + relatedToHeight) > 0;
        exceededLeft = relatedToLeft - clientX > 0;
        exceededRight = clientX > linkedFloatingElementLeft + linkedFloatingElementWidth;
      }

      return exceededTop || exceededBottom || exceededLeft || exceededRight;
    }

    return true;
  };

  var onAnimationEnd = useCallback(function () {
    var hoveredToBoolean = relatedToRef.current.dataset.hovered === 'true';
    var focusedToBoolean = relatedToRef.current.dataset.focused === 'true';
    var isClosingFromMouseLeave = !hoveredToBoolean && triggerToProcess.includes(TRIGGERS.HOVER);
    var isClosingFromBlur = !focusedToBoolean && triggerToProcess.includes(TRIGGERS.FOCUS);
    var isOpeningFromHover = hoveredToBoolean && triggerToProcess.includes(TRIGGERS.HOVER);
    var isOpeningFromFocus = focusedToBoolean && triggerToProcess.includes(TRIGGERS.FOCUS);

    if (triggerToProcess.includes(TRIGGERS.FOCUS)) {
      isClosingFromMouseLeave = isClosingFromMouseLeave && !focusedToBoolean;
    }

    if (triggerToProcess.includes(TRIGGERS.HOVER)) {
      isClosingFromBlur = isClosingFromBlur && !hoveredToBoolean;
    }

    if (animationState === STATES.CLOSING || isClosingFromMouseLeave || isClosingFromBlur) {
      animationCloseRef.current = false;
      setOpenedState(function (prevState) {
        return _objectSpread$1(_objectSpread$1({}, prevState), {}, {
          isMounted: false,
          internalIsOpened: false
        });
      });
      setAnimationState(STATES.CLOSED);
      onClosed();
    } else if (animationState === STATES.OPENING || isOpeningFromHover || isOpeningFromFocus) {
      setOpenedState(function (prevState) {
        return _objectSpread$1(_objectSpread$1({}, prevState), {}, {
          isMounted: true,
          internalIsOpened: true
        });
      });
      linkedFloatingElementContentRef.current.dataset.state = STATES.OPENED;
      setAnimationState(STATES.OPENED);
      onOpened();
    }
  }, [triggerToProcess, onClosed, onOpened, animationState]);
  var onFocusHandler = useCallback(function () {
    relatedToRef.current.dataset.focused = true;
    open();
  }, [open]);
  var onBlurHandler = useCallback(function () {
    relatedToRef.current.dataset.focused = false;
    close();
  }, [close]);
  var onMouseEnterHandler = useCallback(function () {
    relatedToRef.current.dataset.hovered = true;
    open();
  }, [open]);
  var onMouseMoveHandler = useCallback(function (event) {
    var eventTarget = event.target;
    var isCursorOverRelatedTo = relatedToRef.current.contains(eventTarget);

    if (!isCursorOverRelatedTo && isCursorOutsideInteractiveArea(event)) {
      document.removeEventListener('mousemove', onMouseMoveHandler);
      close();
    }
  }, [close]);
  var onHoverClickHandler = useCallback(function () {
    if (isOpened) {
      close();
    }
  }, [isOpened, close]);
  var onMouseLeaveHandler = useCallback(function (event) {
    relatedToRef.current.dataset.hovered = false;

    if (triggerToProcess.includes(TRIGGERS.HOVER)) {
      if (interactive) {
        document.addEventListener('mousemove', onMouseMoveHandler);
        onMouseMoveHandler(event);
      } else {
        close();
      }
    } else {
      close();
    }
  }, [interactive, triggerToProcess, close, onMouseMoveHandler]);
  var onClickHandler = useCallback(function () {
    if (isOpened && [STATES.OPENING, STATES.OPENED].includes(animationState)) {
      // isOpening or isOpened
      close();
    } else if (!isOpened && [STATES.CLOSING, STATES.CLOSED].includes(animationState)) {
      // isClosing or isClosed
      open();
    }
  }, [open, close, isOpened, animationState]);
  var reposition = useCallback(function (isOpenedParam) {
    if (isOpenedParam && linkedFloatingElementRef.current && relatedToRef.current) {
      var containerToProcess = limitsPlacementContainerRef.current;

      if (!isDefinedAndNotNull(containerToProcess)) {
        var closestDialog = relatedToRef.current.closest('[data-is-dialog="true"]');
        containerToProcess = closestDialog;
      }

      var currentAnimationState = linkedFloatingElementContentRef.current.dataset.state;

      var _getPositionByPlaceme = getPositionByPlacement({
        linkedFloatingElement: linkedFloatingElementRef.current,
        relatedToElement: relatedToRef.current,
        placement: placement,
        avoidReposition: avoidReposition,
        sameWidthAsChildren: sameWidthAsChildren,
        container: containerToProcess,
        forceOpenEvenNotFit: forceOpenEvenNotFit,
        forceViewportPlacement: forceViewportPlacement,
        withArrow: withArrow,
        withAppendTo: isDefinedAndNotNull(appendTo)
      }),
          finalTop = _getPositionByPlaceme.top,
          finalLeft = _getPositionByPlaceme.left,
          finalWidth = _getPositionByPlaceme.width,
          finalPlacement = _getPositionByPlaceme.placement,
          lastPlacementVisible = _getPositionByPlaceme.lastPlacementVisible,
          shouldClose = _getPositionByPlaceme.shouldClose;

      if (shouldClose && (!triggerToProcess.includes(TRIGGERS.MANUAL) || triggerToProcess.includes(TRIGGERS.MANUAL) && forceDefaultCloseMethod)) {
        if ([STATES.OPENED, STATES.OPENING].includes(currentAnimationState)) {
          // If the linkedFloatingElement is opened or opening, we have to start the closing animation
          close();
        } else {
          animationCloseRef.current = false; // In case that the linkedFloatingElement is closed or closing, we force to close it

          setOpenedState(function (prevState) {
            return _objectSpread$1(_objectSpread$1({}, prevState), {}, {
              isOpened: false,
              isMounted: false,
              internalIsOpened: false
            });
          });
          setAnimationState(STATES.CLOSED);
        }
      } else if (isNull(finalTop) || isNull(finalLeft) || isNull(finalWidth)) {
        /*
          In case of trigger="manual" without forceDefaultCloseMethod
          and the floating element doesn't fit inside the container,
          we will hide the floating element.
        */
        linkedFloatingElementRef.current.style.visibility = 'hidden';
        linkedFloatingElementRef.current.style.top = '0';
        linkedFloatingElementRef.current.style.left = '0';
      } else {
        var hasTopChanged = parseInt(linkedFloatingElementRef.current.style.top, 10) !== finalTop;
        var hasLeftChanged = parseInt(linkedFloatingElementRef.current.style.left, 10) !== finalLeft;
        var hasWidthChanged = parseInt(linkedFloatingElementRef.current.style.width, 10) !== finalWidth;
        var hasPlacementChanged = linkedFloatingElementRef.current.dataset.placement !== finalPlacement;
        var hasLastPlacementChanged = linkedFloatingElementContentRef.current.dataset.lastPlacementVisible !== lastPlacementVisible;
        linkedFloatingElementRef.current.style.inset = '0px auto auto 0px';
        linkedFloatingElementRef.current.style.position = 'absolute';

        if (hasTopChanged || hasLeftChanged || hasPlacementChanged || hasWidthChanged || hasLastPlacementChanged) {
          linkedFloatingElementRef.current.style.visibility = 'visible';
        }

        if (hasTopChanged || hasLeftChanged) {
          linkedFloatingElementRef.current.style.transform = "translate3d(".concat(finalLeft, "px, ").concat(finalTop, "px, 0px)");
        }

        if (hasPlacementChanged) {
          linkedFloatingElementContentRef.current.dataset.placement = finalPlacement;
        }

        if (lastPlacementVisible && hasLastPlacementChanged) {
          linkedFloatingElementContentRef.current.dataset.lastPlacementVisible = lastPlacementVisible;
        }

        if (sameWidthAsChildren && hasWidthChanged) {
          linkedFloatingElementRef.current.style.width = "".concat(finalWidth, "px");
        } // Only if the previous state was closed or closing


        if ([STATES.CLOSED, STATES.CLOSING].includes(currentAnimationState) && !animationCloseRef.current) {
          linkedFloatingElementContentRef.current.dataset.state = STATES.OPENING;
          setAnimationState(STATES.OPENING);
        }
      }
    }
  }, [placement, appendTo, withArrow, avoidReposition, forceOpenEvenNotFit, triggerToProcess, forceDefaultCloseMethod, forceViewportPlacement, sameWidthAsChildren]);
  var closeCallback = useCallback(function () {
    if (linkedFloatingElementRef.current) {
      var hasFloatingElementChild = linkedFloatingElementRef.current.querySelector('[data-is-floating-element="true"]');

      if (!hasFloatingElementChild && [STATES.OPENED, STATES.OPENING].includes(animationState)) {
        close();
      }
    }
  }, [close, animationState]);
  var onEscape = useCallback(function () {
    if (isFunction(onEscapeFromProps)) {
      onEscapeFromProps({
        close: close,
        floatingElementRef: linkedFloatingElementRef,
        isClosing: animationCloseRef.current
      });
    } else {
      closeCallback();
    }
  }, [closeCallback, onEscapeFromProps, close]);
  var onClickOutside = useCallback(function (event) {
    var isRelatedToClicked = event.target.dataset.relatedLinkedFloatingElement === linkedFloatingElementId.current || relatedToRef.current.contains(event.target);

    if (!isRelatedToClicked) {
      if (isFunction(onClickOutsideFromProps)) {
        onClickOutsideFromProps({
          close: close,
          floatingElementRef: linkedFloatingElementRef,
          isClosing: animationCloseRef.current
        });
      } else {
        closeCallback();
      }
    }
  }, [closeCallback, onClickOutsideFromProps, close]);
  var wasOpened = usePrevious(opened);
  useDisableBackgroundScroll({
    elementToBlock: relatedToRef.current,
    recursive: true,
    enabled: disableBackgroundScroll && isOpened
  });
  useEscape({
    onEscape: onEscape,
    preventClose: !closeOnEscape
  });
  useOnClickOutside({
    elementRef: linkedFloatingElementRef,
    callback: onClickOutside,
    disabled: !closeOnClickOutside
  });
  useMutationObserver({
    elementRef: linkedFloatingElementContentRef,
    callback: function callback(mutations) {
      var newPlacement;
      mutations.forEach(function (mutation) {
        newPlacement = mutation.target.dataset[mutation.attributeName.replace('data-', '')];
      });
      onPlacementChanged(newPlacement);
    },
    options: {
      attributes: true,
      attributeFilter: ['data-placement']
    }
  });
  useEffect(function () {
    var containerToProcess = isString(limitsPlacementContainer) ? document.getElementById(limitsPlacementContainer) : limitsPlacementContainer;
    limitsPlacementContainerRef.current = containerToProcess;
  }, [limitsPlacementContainer]);
  useEffect(function () {
    var relatedToNode = relatedToRef.current;

    if (relatedToNode && !disabled) {
      if (triggerToProcess.includes(TRIGGERS.HOVER)) {
        relatedToNode.addEventListener('mouseenter', onMouseEnterHandler);
        relatedToNode.addEventListener('mouseleave', onMouseLeaveHandler);
        relatedToNode.addEventListener('mousedown', onHoverClickHandler);
      }

      if (triggerToProcess.includes(TRIGGERS.CLICK)) {
        relatedToNode.addEventListener('click', onClickHandler);
      }

      if (triggerToProcess.includes(TRIGGERS.FOCUS)) {
        relatedToNode.addEventListener('focus', onFocusHandler);
        relatedToNode.addEventListener('blur', onBlurHandler);
      }

      if (disableBackgroundScroll) {
        relatedToNode.addEventListener('blur', onBlurHandler);
      }
    }

    return function () {
      if (!disabled) {
        if (triggerToProcess.includes(TRIGGERS.HOVER)) {
          relatedToNode.removeEventListener('mouseenter', onMouseEnterHandler);
          relatedToNode.removeEventListener('mouseleave', onMouseLeaveHandler);
          relatedToNode.removeEventListener('mousedown', onHoverClickHandler);
        }

        if (triggerToProcess.includes(TRIGGERS.CLICK)) {
          relatedToNode.removeEventListener('click', onClickHandler);
        }

        if (triggerToProcess.includes(TRIGGERS.FOCUS)) {
          relatedToNode.removeEventListener('focus', onFocusHandler);
          relatedToNode.removeEventListener('blur', onBlurHandler);
        }

        if (disableBackgroundScroll) {
          relatedToNode.removeEventListener('blur', onBlurHandler);
        }
      }
    };
  }, [disabled, disableBackgroundScroll, triggerToProcess, relatedTo, onClickHandler, onFocusHandler, onBlurHandler, onMouseEnterHandler, onHoverClickHandler, onMouseLeaveHandler]);
  useEffect(function () {
    if (triggerToProcess.includes(TRIGGERS.MANUAL)) {
      if (opened && !wasOpened) {
        open();
      } else if (wasOpened && !opened) {
        close();
      }
    }
  }, [opened, triggerToProcess, wasOpened, open, close]);
  useEffect(function () {
    if (isOpened || internalIsOpened && isMounted) {
      reposition(isOpened);
    }
  }, [internalIsOpened, isMounted, isOpened]);
  useEffect(function () {
    var relatedToElement = relatedToRef.current;
    var linkedFloatingElementContent = linkedFloatingElementContentRef.current;
    var resizeObserver = new ResizeObserver(function () {
      var shouldReposition = true;
      var floatingElement = linkedFloatingElementContentRef.current;

      if (floatingElement) {
        var isClosing = floatingElement.dataset.state === STATES.CLOSING; // In case that the element is resizing and the linked floating element is closing, we should avoid a reposition

        if (isClosing) {
          shouldReposition = false;
        }
      }

      if (shouldReposition) {
        reposition(isOpened);
      }
    });
    resizeObserver.observe(relatedToElement);

    if (linkedFloatingElementContent) {
      resizeObserver.observe(linkedFloatingElementContent);
    }

    var scrollableContainers = getScrollableContainersByElement(relatedToElement);
    scrollableContainers.forEach(function (eachContainer) {
      eachContainer.addEventListener('scroll', reposition);
      eachContainer.addEventListener('resize', reposition);
    });
    window.addEventListener('scroll', reposition);
    window.addEventListener('resize', reposition);
    return function () {
      resizeObserver.unobserve(relatedToElement);

      if (linkedFloatingElementContent) {
        resizeObserver.unobserve(linkedFloatingElementContent);
      }

      scrollableContainers.forEach(function (eachContainer) {
        eachContainer.removeEventListener('scroll', reposition);
        eachContainer.removeEventListener('resize', reposition);
      });
      window.removeEventListener('scroll', reposition);
      window.removeEventListener('resize', reposition);
    };
  }, [isOpened, disableBackgroundScroll, reposition]);
  useImperativeHandle(ref, function () {
    return {
      linkedFloatingElement: linkedFloatingElementRef.current,
      relatedTo: relatedToRef.current
    };
  });
  var classnames = ['gz-linked-floating-element'];

  if (withArrow) {
    classnames.push('with-arrow');
  }

  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/React.cloneElement(relatedTo, {
      ref: relatedToRef,
      'data-related-linked-floating-element': linkedFloatingElementId.current
    }), (isOpened || internalIsOpened && isMounted) && /*#__PURE__*/jsx(FloatingElement, _objectSpread$1(_objectSpread$1({
      ref: linkedFloatingElementRef,
      className: classnames.join(' '),
      appendTo: appendTo,
      onAnimationEnd: onAnimationEnd // eslint-disable-next-line react/jsx-props-no-spreading

    }, dataAttributes), {}, {
      "data-linked-floating-element-id": linkedFloatingElementId.current,
      children: /*#__PURE__*/jsxs("div", {
        ref: linkedFloatingElementContentRef,
        className: "gz-linked-floating-element__content ".concat(className),
        "data-animation": animation,
        "data-state": animationState,
        children: [contentToRender({
          open: open,
          close: close
        }), withArrow && /*#__PURE__*/jsx("div", {
          className: "gz-linked-floating-element__arrow"
        })]
      })
    }))]
  });
});
LinkedFloatingElement$1.ANIMATIONS = ANIMATIONS;
LinkedFloatingElement$1.PLACEMENTS = PLACEMENTS;
LinkedFloatingElement$1.TRIGGERS = TRIGGERS;
LinkedFloatingElement$1.COMPONENT_ID = 'Gearz.LinkedFloatingElement';
LinkedFloatingElement$1.displayName = LinkedFloatingElement$1.COMPONENT_ID;
LinkedFloatingElement$1.propTypes = _objectSpread$1(_objectSpread$1({}, FloatingElement.propTypes), {}, {
  children: PropTypes.oneOfTypeWithDescription([PropTypes.string, PropTypes.element], 'Content of the linked floating element. In case of function, `({ open, close }) => {}`'),
  animation: PropTypes.oneOfWithDescription(Object.values(ANIMATIONS), 'Animation to use on open/close the linked floating element.'),
  appendTo: PropTypes.oneOfTypeWithDescription([PropTypes.string, PropTypes.instanceOf(getSafeHtmlElement())], 'Destination element to append the linked floating element.'),
  avoidReposition: PropTypes.boolWithDescription('Flag to avoid the automatic position recalculation.'),
  disableBackgroundScroll: PropTypes.boolWithDescription('Flag to disable the background scroll.'),
  delay: PropTypes.oneOfTypeWithDescription([PropTypes.number, PropTypes.arrayOf(PropTypes.number)], 'Time (in milliseconds) to wait until open/close the linked floating element. Can be an array of `[delayStart, delayEnd]` or a `number`.'),
  forceViewportPlacement: PropTypes.boolWithDescription('Flag to force the placement calculation based on the viewport.'),
  interactive: PropTypes.boolWithDescription('Flag to make the linked floating element interactive, allowing hover and click inside it.'),
  content: PropTypes.oneOfTypeWithDescription([PropTypes.node, PropTypes.func], 'Content of the linked floating element. In case of function: `({ open, close }) => {}`'),
  limitsPlacementContainer: PropTypes.oneOfTypeWithDescription([PropTypes.string, PropTypes.instanceOf(getSafeHtmlElement())], 'Container of the linked floating element. Passing it, the placement calculation will be based on it.'),
  sameWidthAsChildren: PropTypes.boolWithDescription('Flag to fix the width of the linked floating element the same as the its trigger element.'),
  shouldWrapChildren: PropTypes.boolWithDescription('Flag to wrap the trigger of the linked floating element inside a `<span>`. Useful for disabled trigger elements.'),
  forceOpenEvenNotFit: PropTypes.boolWithDescription('Flag to force open the floating element even it doesn\'t fit in the container.'),
  forceDefaultCloseMethod: PropTypes.boolWithDescription('Flag to force the close behaviour when the floating element doesn\'t fit in the container. Useful for trigger="manual".'),
  closeOnEscape: PropTypes.boolWithDescription('Flag to deactivate the close on escape key pressed.'),
  closeOnClickOutside: PropTypes.boolWithDescription('Flag to deactivate the close on click outside the dialog.'),
  placement: PropTypes.oneOfWithDescription(Object.values(PLACEMENTS), 'Placement of the linked floating element.'),
  trigger: PropTypes.oneOfTypeWithDescription([PropTypes.oneOf(Object.values(TRIGGERS)), PropTypes.arrayOf(PropTypes.oneOf([TRIGGERS.CLICK, TRIGGERS.FOCUS, TRIGGERS.HOVER]))], 'Trigger/s to show the linked floating element.'),
  opened: PropTypes.boolWithDescription('Flag to determine if the linked floating element is opened/closed, when trigger="manual".'),
  disabled: PropTypes.boolWithDescription('Flag to deactivate the linked floating element opening. This prop has no effect with trigger="manual".'),
  withArrow: PropTypes.boolWithDescription('Flag to add/remove an arrow.'),
  onPlacementChanged: PropTypes.funcWithDescription('Callback called when the placement has changed. `(newPlacement) => {}`'),
  onOpened: PropTypes.funcWithDescription('Callback called when the opening animation ends.'),
  onClose: PropTypes.funcWithDescription('Callback called when the closing animation starts.'),
  onClosed: PropTypes.funcWithDescription('Callback called when the closing animations ends.'),
  onClickOutside: PropTypes.funcWithDescription('Custom callback called when the user click outside the linked floating element. `({ close, floatingElementRef, isClosing }) => {}`'),
  onEscape: PropTypes.funcWithDescription('Custom callback called when the user press the escape key. `({ close, floatingElementRef, isClosing }) => {}`')
});
LinkedFloatingElement$1.defaultProps = _objectSpread$1(_objectSpread$1({}, FloatingElement.defaultProps), {}, {
  animation: ANIMATIONS.FADE,
  appendTo: null,
  avoidReposition: false,
  forceViewportPlacement: false,
  disableBackgroundScroll: false,
  interactive: false,
  content: null,
  delay: [0, 0],
  limitsPlacementContainer: null,
  shouldWrapChildren: false,
  sameWidthAsChildren: false,
  forceOpenEvenNotFit: false,
  forceDefaultCloseMethod: false,
  closeOnEscape: true,
  closeOnClickOutside: true,
  placement: PLACEMENTS.AUTO,
  trigger: [TRIGGERS.HOVER, TRIGGERS.FOCUS],
  opened: false,
  disabled: false,
  withArrow: false,
  onPlacementChanged: noop,
  onOpened: noop,
  onClose: noop,
  onClosed: noop,
  onClickOutside: null,
  onEscape: null
});

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var DEFAULT_ANIMATION_DURATION = [300, 300];

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var getAnimationDuration = function getAnimationDuration() {
  var animationDurationProp = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [0, 0];

  if (isNumber(animationDurationProp) && Number.isInteger(animationDurationProp)) {
    return {
      animationDurationStart: animationDurationProp,
      animationDurationEnd: animationDurationProp
    };
  }

  return {
    animationDurationStart: animationDurationProp[0],
    animationDurationEnd: animationDurationProp[1]
  };
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var fadeInAnimation = keyframes(["0%{opacity:0;}100%{opacity:1;}"]);
var fadeOutAnimation = keyframes(["0%{opacity:1;}100%{opacity:0}"]);

var getFadeAnimation = function getFadeAnimation() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$duration = _ref.duration,
      duration = _ref$duration === void 0 ? DEFAULT_ANIMATION_DURATION : _ref$duration;

  var _getAnimationDuration = getAnimationDuration(duration),
      animationDurationStart = _getAnimationDuration.animationDurationStart,
      animationDurationEnd = _getAnimationDuration.animationDurationEnd;

  return css(["&[data-animation=\"", "\"]{&[data-state=\"", "\"]{opacity:0;}&[data-state=\"", "\"]{opacity:1;}&[data-state=\"", "\"]{animation:", "ms ", ";}&[data-state=\"", "\"]{animation:", "ms ", ";}}"], ANIMATIONS.FADE, STATES.CLOSED, STATES.OPENED, STATES.OPENING, animationDurationStart, fadeInAnimation, STATES.CLOSING, animationDurationEnd, fadeOutAnimation);
}; // eslint-disable-next-line import/prefer-default-export

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var noneInAnimation = keyframes(["0%{opacity:0;}100%{opacity:1;}"]);
var noneOutAnimation = keyframes(["0%{opacity:1;}100%{opacity:0}"]);

var getNoneAnimation = function getNoneAnimation() {
  return css(["&[data-animation=\"", "\"]{&[data-state=\"", "\"]{opacity:0;}&[data-state=\"", "\"]{opacity:1;}&[data-state=\"", "\"]{animation:1ms ", ";}&[data-state=\"", "\"]{animation:1ms ", ";}}"], ANIMATIONS.NONE, STATES.CLOSED, STATES.OPENED, STATES.OPENING, noneInAnimation, STATES.CLOSING, noneOutAnimation);
}; // eslint-disable-next-line import/prefer-default-export

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var shiftAwayTopInAnimation = keyframes(["0%{opacity:0;transform:translateY(20%);}100%{opacity:1;transform:translateY(0%);}"]);
var shiftAwayTopOutAnimation = keyframes(["0%{opacity:1;transform:translateY(0%);}100%{opacity:0;transform:translateY(20%);}"]);
var shiftAwayRightInAnimation = keyframes(["0%{opacity:0;transform:translateX(-20%);}100%{opacity:1;transform:translateX(0%);}"]);
var shiftAwayRightOutAnimation = keyframes(["0%{opacity:1;transform:translateX(0%);}100%{opacity:0;transform:translateX(-20%);}"]);
var shiftAwayBottomInAnimation = keyframes(["0%{opacity:0;transform:translateY(-20%);}100%{opacity:1;transform:translateY(0%);}"]);
var shiftAwayBottomOutAnimation = keyframes(["0%{opacity:1;transform:translateY(0%);}100%{opacity:0;transform:translateY(-20%);}"]);
var shiftAwayLeftInAnimation = keyframes(["0%{opacity:0;transform:translateX(20%);}100%{opacity:1;transform:translateX(0%);}"]);
var shiftAwayLeftOutAnimation = keyframes(["0%{opacity:1;transform:translateX(0%);}100%{opacity:0;transform:translateX(20%);}"]);

var getShiftAwayAnimation = function getShiftAwayAnimation() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$duration = _ref.duration,
      duration = _ref$duration === void 0 ? DEFAULT_ANIMATION_DURATION : _ref$duration;

  var _getAnimationDuration = getAnimationDuration(duration),
      animationDurationStart = _getAnimationDuration.animationDurationStart,
      animationDurationEnd = _getAnimationDuration.animationDurationEnd;

  return css(["&[data-animation=\"", "\"]{&[data-state=\"", "\"]{opacity:0;}&[data-state=\"", "\"]{opacity:1;}&[data-state=\"", "\"]{&[data-placement=\"", "\"],&[data-placement=\"", "\"],&[data-placement=\"", "\"]{animation:", "ms ", ";}&[data-placement=\"", "\"],&[data-placement=\"", "\"],&[data-placement=\"", "\"]{animation:", "ms ", ";}&[data-placement=\"", "\"],&[data-placement=\"", "\"],&[data-placement=\"", "\"]{animation:", "ms ", ";}&[data-placement=\"", "\"],&[data-placement=\"", "\"],&[data-placement=\"", "\"]{animation:", "ms ", ";}}&[data-state=\"", "\"]{&[data-placement=\"", "\"],&[data-placement=\"", "\"],&[data-placement=\"", "\"]{animation:", "ms ", ";}&[data-placement=\"", "\"],&[data-placement=\"", "\"],&[data-placement=\"", "\"]{animation:", "ms ", ";}&[data-placement=\"", "\"],&[data-placement=\"", "\"],&[data-placement=\"", "\"]{animation:", "ms ", ";}&[data-placement=\"", "\"],&[data-placement=\"", "\"],&[data-placement=\"", "\"]{animation:", "ms ", ";}}}"], ANIMATIONS.SHIFT_AWAY, STATES.CLOSED, STATES.OPENED, STATES.OPENING, PLACEMENTS.TOP, PLACEMENTS.TOP_LEFT, PLACEMENTS.TOP_RIGHT, animationDurationStart, shiftAwayTopInAnimation, PLACEMENTS.RIGHT, PLACEMENTS.RIGHT_BOTTOM, PLACEMENTS.RIGHT_TOP, animationDurationStart, shiftAwayRightInAnimation, PLACEMENTS.BOTTOM, PLACEMENTS.BOTTOM_LEFT, PLACEMENTS.BOTTOM_RIGHT, animationDurationStart, shiftAwayBottomInAnimation, PLACEMENTS.LEFT, PLACEMENTS.LEFT_BOTTOM, PLACEMENTS.LEFT_TOP, animationDurationStart, shiftAwayLeftInAnimation, STATES.CLOSING, PLACEMENTS.TOP, PLACEMENTS.TOP_LEFT, PLACEMENTS.TOP_RIGHT, animationDurationEnd, shiftAwayTopOutAnimation, PLACEMENTS.RIGHT, PLACEMENTS.RIGHT_BOTTOM, PLACEMENTS.RIGHT_TOP, animationDurationEnd, shiftAwayRightOutAnimation, PLACEMENTS.BOTTOM, PLACEMENTS.BOTTOM_LEFT, PLACEMENTS.BOTTOM_RIGHT, animationDurationEnd, shiftAwayBottomOutAnimation, PLACEMENTS.LEFT, PLACEMENTS.LEFT_BOTTOM, PLACEMENTS.LEFT_TOP, animationDurationEnd, shiftAwayLeftOutAnimation);
}; // eslint-disable-next-line import/prefer-default-export

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var getAnimation = function getAnimation() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$animation = _ref.animation,
      animation = _ref$animation === void 0 ? ANIMATIONS.FADE : _ref$animation,
      _ref$duration = _ref.duration,
      duration = _ref$duration === void 0 ? DEFAULT_ANIMATION_DURATION : _ref$duration;

  var prerrequisitesCSS;
  var animationCSS;

  switch (animation) {
    default:
    case ANIMATIONS.FADE:
      animationCSS = getFadeAnimation({
        duration: duration
      });
      break;

    case ANIMATIONS.NONE:
      animationCSS = getNoneAnimation();
      break;

    case ANIMATIONS.SHIFT_AWAY:
      prerrequisitesCSS = css(["&[data-placement=\"", "\"],&[data-placement=\"", "\"],&[data-placement=\"", "\"]{transform:translateX(0%);}&[data-placement=\"", "\"],&[data-placement=\"", "\"],&[data-placement=\"", "\"]{transform:translateY(0%);}"], PLACEMENTS.RIGHT, PLACEMENTS.RIGHT_BOTTOM, PLACEMENTS.RIGHT_TOP, PLACEMENTS.BOTTOM, PLACEMENTS.BOTTOM_LEFT, PLACEMENTS.BOTTOM_RIGHT);
      animationCSS = getShiftAwayAnimation({
        duration: duration
      });
      break;
  }

  return css(["", " ", ""], prerrequisitesCSS, animationCSS);
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var getTopLeftArrow = function getTopLeftArrow() {
  return css(["&[data-placement=", "]{.gz-linked-floating-element__arrow{background-color:#fff;border-bottom:1px solid #ccc;border-right:1px solid #ccc;bottom:-5px;height:9px;left:10px;position:absolute;transform:rotate(45deg);width:9px;}}"], PLACEMENTS.TOP_LEFT);
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var getTopArrow = function getTopArrow() {
  return css(["&[data-placement=", "]{.gz-linked-floating-element__arrow{background-color:#fff;border-bottom:1px solid #ccc;border-right:1px solid #ccc;bottom:-5px;height:9px;left:calc(50% - 5px);position:absolute;transform:rotate(45deg);width:9px;}}"], PLACEMENTS.TOP);
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var getTopRightArrow = function getTopRightArrow() {
  return css(["&[data-placement=", "]{.gz-linked-floating-element__arrow{background-color:#fff;border-bottom:1px solid #ccc;border-right:1px solid #ccc;bottom:-5px;height:9px;position:absolute;right:10px;transform:rotate(45deg);width:9px;}}"], PLACEMENTS.TOP_RIGHT);
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var getRightTopArrow = function getRightTopArrow() {
  return css(["&[data-placement=", "]{.gz-linked-floating-element__arrow{background-color:#fff;border-left:1px solid #ccc;border-top:1px solid #ccc;height:9px;left:-5px;position:absolute;top:10px;transform:rotate(-45deg);width:9px;}}"], PLACEMENTS.RIGHT_TOP);
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var getRightArrow = function getRightArrow() {
  return css(["&[data-placement=", "]{.gz-linked-floating-element__arrow{background-color:#fff;border-left:1px solid #ccc;border-top:1px solid #ccc;height:9px;left:-5px;position:absolute;top:calc(50% - 5px);transform:rotate(-45deg);width:9px;}}"], PLACEMENTS.RIGHT);
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var getRightBottomArrow = function getRightBottomArrow() {
  return css(["&[data-placement=", "]{.gz-linked-floating-element__arrow{background-color:#fff;border-left:1px solid #ccc;border-top:1px solid #ccc;bottom:10px;height:9px;left:-5px;position:absolute;transform:rotate(-45deg);width:9px;}}"], PLACEMENTS.RIGHT_BOTTOM);
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var getBottomLeftArrow = function getBottomLeftArrow() {
  return css(["&[data-placement=", "]{.gz-linked-floating-element__arrow{background-color:#fff;border-left:1px solid #ccc;border-top:1px solid #ccc;height:9px;left:10px;position:absolute;top:-5px;transform:rotate(45deg);width:9px;}}"], PLACEMENTS.BOTTOM_LEFT);
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var getBottomArrow = function getBottomArrow() {
  return css(["&[data-placement=", "]{.gz-linked-floating-element__arrow{background-color:#fff;border-left:1px solid #ccc;border-top:1px solid #ccc;height:9px;left:50%;position:absolute;top:-5px;transform:rotate(45deg);width:9px;}}"], PLACEMENTS.BOTTOM);
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var getBottomLRightArrow = function getBottomLRightArrow() {
  return css(["&[data-placement=", "]{.gz-linked-floating-element__arrow{background-color:#fff;border-left:1px solid #ccc;border-top:1px solid #ccc;height:9px;position:absolute;right:10px;top:-5px;transform:rotate(45deg);width:9px;}}"], PLACEMENTS.BOTTOM_RIGHT);
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var getLeftTopArrow = function getLeftTopArrow() {
  return css(["&[data-placement=", "]{.gz-linked-floating-element__arrow{background-color:#fff;border-right:1px solid #ccc;border-top:1px solid #ccc;height:9px;position:absolute;right:-5px;top:10px;transform:rotate(45deg);width:9px;}}"], PLACEMENTS.LEFT_TOP);
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var getLeftArrow = function getLeftArrow() {
  return css(["&[data-placement=", "]{.gz-linked-floating-element__arrow{background-color:#fff;border-right:1px solid #ccc;border-top:1px solid #ccc;height:9px;position:absolute;right:-5px;top:calc(50% - 5px);transform:rotate(45deg);width:9px;}}"], PLACEMENTS.LEFT);
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var getLeftBottomArrow = function getLeftBottomArrow() {
  return css(["&[data-placement=", "]{.gz-linked-floating-element__arrow{background-color:#fff;border-right:1px solid #ccc;border-top:1px solid #ccc;bottom:10px;height:9px;position:absolute;right:-5px;transform:rotate(45deg);width:9px;}}"], PLACEMENTS.LEFT_BOTTOM);
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var getArrowByPlacement = function getArrowByPlacement() {
  return css(["", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", ""], getTopLeftArrow(), getTopArrow(), getTopRightArrow(), getRightTopArrow(), getRightArrow(), getRightBottomArrow(), getBottomLeftArrow(), getBottomArrow(), getBottomLRightArrow(), getLeftTopArrow(), getLeftArrow(), getLeftBottomArrow());
};

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var LinkedFloatingElement = styled(LinkedFloatingElement$1).withConfig({
  componentId: "gz-floating-element-1679042011924__sc-1c67i9d-0"
})(["background-color:#fff;border:1px solid #ccc;border-radius:4px;padding:8px;position:relative;will-change:transform,visibility;.gz-linked-floating-element__arrow{border:1px solid transparent;height:9px;position:absolute;width:9px;}", " ", ""], function (_ref) {
  var animation = _ref.animation,
      animationDuration = _ref.animationDuration;
  return getAnimation({
    animation: animation,
    duration: animationDuration
  });
}, function (_ref2) {
  var withArrow = _ref2.withArrow;
  return withArrow && getArrowByPlacement();
});
LinkedFloatingElement.displayName = LinkedFloatingElement$1.displayName;
LinkedFloatingElement.propTypes = _objectSpread(_objectSpread({}, LinkedFloatingElement$1.propTypes), {}, {
  animationDuration: PropTypes.oneOfTypeWithDescription([PropTypes.number, PropTypes.arrayOf(PropTypes.number)], 'Duration (in milliseconds) of the animation.. Can be an array of `[animationStart, animationEnd]` or a `number`.')
});
LinkedFloatingElement.defaultProps = _objectSpread(_objectSpread({}, LinkedFloatingElement$1.defaultProps), {}, {
  animationDuration: DEFAULT_ANIMATION_DURATION
});

export { LinkedFloatingElement as L };
